import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, Modal } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminManager from '../services/AdminManager';
import OrgManager from '../services/OrgManager';
import { FaUserPlus, FaList, FaChartBar, FaDatabase, FaArrowLeft, FaCheck, FaTimes, FaBuilding, FaUsers, FaFire, FaSnowflake, FaMagic, FaCamera } from 'react-icons/fa';
import { useTheme } from '../theme/ThemeContext';

let THEME = {};
const getStyles = () => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: THEME.bg,
    },
    header: {
        backgroundColor: THEME.bgCard,
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 15,
    },
    backButtonText: {
        color: THEME.textPrimary,
        fontSize: 16,
        fontWeight: '500',
    },
    headerTitle: {
        fontSize: 28,
        fontWeight: 'bold',
        color: THEME.textPrimary,
    },
    tabsContainer: {
        flexDirection: 'row',
        backgroundColor: THEME.bgCard,
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
    },
    tab: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 15,
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
    },
    activeTab: {
        borderBottomColor: '#4DA6FF',
    },
    tabText: {
        color: THEME.textMuted,
        fontSize: 14,
        fontWeight: '600',
    },
    activeTabText: {
        color: '#4DA6FF',
    },
    content: {
        flex: 1,
    },
    section: {
        padding: 20,
    },
    sectionTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        color: THEME.textPrimary,
        marginBottom: 8,
    },
    sectionSubtitle: {
        fontSize: 14,
        color: THEME.textMuted,
        marginBottom: 24,
    },
    form: {
        backgroundColor: THEME.bgCard,
        borderRadius: 16,
        padding: 20,
        marginBottom: 24,
    },
    inputGroup: {
        marginBottom: 20,
    },
    label: {
        fontSize: 14,
        fontWeight: '600',
        color: THEME.textPrimary,
        marginBottom: 8,
    },
    helperText: {
        fontSize: 12,
        color: THEME.textMuted,
        marginBottom: 8,
    },
    input: {
        backgroundColor: THEME.bg,
        borderRadius: 8,
        padding: 12,
        color: THEME.textPrimary,
        fontSize: 14,
        borderWidth: 1,
        borderColor: THEME.border,
    },
    textArea: {
        minHeight: 80,
        textAlignVertical: 'top',
    },
    errorContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(255, 68, 68, 0.1)',
        padding: 12,
        borderRadius: 8,
        marginBottom: 16,
    },
    errorText: {
        color: '#ff4444',
        fontSize: 14,
    },
    successContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(68, 255, 68, 0.1)',
        padding: 12,
        borderRadius: 8,
        marginBottom: 16,
    },
    successText: {
        color: '#44ff44',
        fontSize: 14,
    },
    submitButton: {
        backgroundColor: '#4DA6FF',
        borderRadius: 8,
        padding: 16,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
    },
    submitButtonDisabled: {
        opacity: 0.6,
    },
    submitButtonText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
    listsContainer: {
        marginTop: 24,
    },
    listCard: {
        backgroundColor: THEME.bgCard,
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
    },
    listCardTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: THEME.textPrimary,
        marginBottom: 4,
    },
    listCardSubtitle: {
        fontSize: 14,
        color: THEME.textMuted,
        marginBottom: 8,
    },
    listCardDate: {
        fontSize: 12,
        color: THEME.textMuted,
    },
    emptyText: {
        color: THEME.textMuted,
        fontSize: 14,
        textAlign: 'center',
        marginTop: 20,
    },
    statsCard: {
        backgroundColor: THEME.bgCard,
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
    },
    statsHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
    },
    statsCardTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: THEME.textPrimary,
    },
    statsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
    },
    statItem: {
        flex: 1,
        minWidth: 120,
        backgroundColor: THEME.bg,
        borderRadius: 12,
        padding: 16,
        alignItems: 'center',
    },
    statValue: {
        fontSize: 32,
        fontWeight: 'bold',
        color: '#4DA6FF',
        marginBottom: 4,
    },
    statLabel: {
        fontSize: 12,
        color: THEME.textMuted,
        textAlign: 'center',
    },
    refreshButton: {
        backgroundColor: THEME.bgCard,
        borderRadius: 8,
        padding: 16,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: THEME.border,
    },
    refreshButtonText: {
        color: '#4DA6FF',
        fontSize: 16,
        fontWeight: '600',
    },
    infoBox: {
        backgroundColor: 'rgba(77, 166, 255, 0.1)',
        borderRadius: 12,
        padding: 16,
        marginTop: 24,
        borderWidth: 1,
        borderColor: 'rgba(77, 166, 255, 0.3)',
    },
    infoTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#4DA6FF',
        marginBottom: 12,
    },
    infoText: {
        fontSize: 14,
        color: THEME.textSecondary,
        marginBottom: 6,
        paddingLeft: 8,
    },
    assignContainer: {
        flexDirection: 'row',
        gap: 20,
        marginBottom: 24,
    },
    assignColumn: {
        flex: 1,
        backgroundColor: THEME.bgCard,
        borderRadius: 16,
        padding: 16,
    },
    assignColumnTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: THEME.textPrimary,
        marginBottom: 16,
    },
    selectionList: {
        maxHeight: 400,
    },
    selectionItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: 12,
        backgroundColor: THEME.bg,
        borderRadius: 8,
        marginBottom: 8,
        borderWidth: 2,
        borderColor: 'transparent',
    },
    selectionItemSelected: {
        borderColor: '#4DA6FF',
        backgroundColor: 'rgba(77, 166, 255, 0.1)',
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: '#4DA6FF',
        marginRight: 12,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
    },
    selectionItemContent: {
        flex: 1,
    },
    selectionItemTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: THEME.textPrimary,
        marginBottom: 4,
    },
    selectionItemSubtitle: {
        fontSize: 12,
        color: THEME.textMuted,
    },
    searchInput: {
        backgroundColor: THEME.bg,
        borderRadius: 8,
        padding: 12,
        color: THEME.textPrimary,
        fontSize: 14,
        borderWidth: 1,
        borderColor: THEME.border,
        marginBottom: 12,
    },
    table: {
        backgroundColor: THEME.bgCard,
        borderRadius: 16,
        padding: 16,
        minWidth: '100%',
    },
    tableHeader: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
        paddingBottom: 12,
        marginBottom: 12,
    },
    tableHeaderText: {
        color: THEME.textMuted,
        fontSize: 12,
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
        alignItems: 'center',
    },
    tableCellText: {
        color: THEME.textSecondary,
        fontSize: 14,
    },
    tableCellTextBold: {
        color: THEME.textPrimary,
        fontSize: 14,
        fontWeight: '600',
    },
    tableCellTextIds: {
        color: THEME.textMuted,
        fontSize: 11,
        marginTop: 2,
    },
    statsRow: {
        flexDirection: 'row',
        gap: 20,
        flexWrap: 'wrap',
    },
    flexCard: {
        flex: 1,
        minWidth: 300,
    },
    rowBetween: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderBottomColor: THEME.border,
    },
    rowLabel: {
        color: THEME.textSecondary,
        fontSize: 14,
    },
    rowValue: {
        color: THEME.textPrimary,
        fontWeight: 'bold',
        fontSize: 14,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: THEME.overlay,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        backgroundColor: THEME.modalBg,
        borderRadius: 12,
        padding: 24,
        width: '90%',
        maxWidth: 600,
        maxHeight: '80%',
        shadowColor: '#000',
        shadowOpacity: 0.3,
        shadowRadius: 10,
        elevation: 10,
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    modalTitle: {
        color: THEME.textPrimary,
        fontSize: 20,
        fontWeight: 'bold',
    },
    closeButton: {
        padding: 8,
    },
    activityItem: {
        backgroundColor: THEME.bgCard,
        padding: 16,
        borderRadius: 8,
        marginBottom: 10,
    },
    activityDate: {
        color: THEME.textMuted,
        fontSize: 12,
        marginBottom: 4,
    },
    activityAction: {
        color: '#4DA6FF',
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 4,
    },
    activityDetails: {
        color: THEME.textSecondary,
        fontSize: 12,
    },
    actionButton: {
        backgroundColor: 'rgba(77, 166, 255, 0.2)',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 6,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(77, 166, 255, 0.4)',
    },
    actionButtonText: {
        color: '#4DA6FF',
        fontSize: 12,
        fontWeight: '600',
    }
});
let styles = getStyles();

const AdminPanelScreen = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { theme } = useTheme();
    Object.assign(THEME, theme);
    styles = getStyles();
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('register'); // register, listeners, suggested_lists, stats

    // Firebase registration state
    const [newFirebaseUser, setNewFirebaseUser] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [firebaseRegisterLoading, setFirebaseRegisterLoading] = useState(false);
    const [firebaseRegisterSuccess, setFirebaseRegisterSuccess] = useState(false);
    const [firebaseRegisterError, setFirebaseRegisterError] = useState('');

    // Listener registration state
    const [newListener, setNewListener] = useState({
        id: '',
        username: '',
        email: '',
        general_request: ''
    });
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerSuccess, setRegisterSuccess] = useState(false);
    const [registerError, setRegisterError] = useState('');

    // Suggested list state
    const [suggestedLists, setSuggestedLists] = useState([]);
    const [suggestedListsLoading, setSuggestedListsLoading] = useState(false);
    const [newSuggestedList, setNewSuggestedList] = useState({
        list_name: '',
        list_prompt: '',
        usernames: ''
    });
    const [createListLoading, setCreateListLoading] = useState(false);
    const [createListSuccess, setCreateListSuccess] = useState(false);
    const [createListError, setCreateListError] = useState('');

    // Stats state
    const [messageStats, setMessageStats] = useState(null);
    const [dbStats, setDbStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Assign lists state
    const [availableLists, setAvailableLists] = useState([]);
    const [availableListeners, setAvailableListeners] = useState([]);
    const [selectedListIds, setSelectedListIds] = useState([]);
    const [selectedListenerIds, setSelectedListenerIds] = useState([]);
    const [listenerSearchEmail, setListenerSearchEmail] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignSuccess, setAssignSuccess] = useState(false);
    const [assignError, setAssignError] = useState('');
    const [assignResult, setAssignResult] = useState(null);

    // Listeners Data state
    const [listenersData, setListenersData] = useState([]);
    const [listenersDataLoading, setListenersDataLoading] = useState(false);

    // Detailed Stats state
    const [detailedStats, setDetailedStats] = useState(null);

    // Activity Modal state
    const [isActivityModalVisible, setIsActivityModalVisible] = useState(false);
    const [selectedListenerActivity, setSelectedListenerActivity] = useState(null);
    const [activityLoading, setActivityLoading] = useState(false);
    const [selectedListenerName, setSelectedListenerName] = useState('');

    // Organizations tab state
    const [orgs, setOrgs] = useState([]);
    const [orgsLoading, setOrgsLoading] = useState(false);
    const [orgSearch, setOrgSearch] = useState('');
    const [expandedOrgId, setExpandedOrgId] = useState(null);
    const [expandedOrgDetail, setExpandedOrgDetail] = useState(null);
    const [expandedOrgDetailLoading, setExpandedOrgDetailLoading] = useState(false);

    // Create org form state
    const [newOrg, setNewOrg] = useState({ name: '', slug: '', plan: 'free' });
    const [newOrgOwnerSearch, setNewOrgOwnerSearch] = useState('');
    const [newOrgOwnerResults, setNewOrgOwnerResults] = useState([]);
    const [newOrgOwner, setNewOrgOwner] = useState(null);
    const [createOrgLoading, setCreateOrgLoading] = useState(false);
    const [createOrgSuccess, setCreateOrgSuccess] = useState('');
    const [createOrgError, setCreateOrgError] = useState('');

    // Edit org state
    const [editingOrgId, setEditingOrgId] = useState(null);
    const [editingOrgData, setEditingOrgData] = useState({});
    const [editOrgLoading, setEditOrgLoading] = useState(false);

    // Delete org state
    const [deleteOrgConfirmId, setDeleteOrgConfirmId] = useState(null);
    const [deleteOrgLoading, setDeleteOrgLoading] = useState(false);

    // Add member to org state
    const [addMemberSearch, setAddMemberSearch] = useState({});
    const [addMemberResults, setAddMemberResults] = useState({});
    const [addMemberRole, setAddMemberRole] = useState({});
    const [addMemberLoading, setAddMemberLoading] = useState({});

    // Analytics state
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
    const [analyticsSort, setAnalyticsSort] = useState('actions_7d');
    const [analyticsSearch, setAnalyticsSearch] = useState('');

    // Single listener analytics state
    const [listenerAnalytics, setListenerAnalytics] = useState(null);
    const [listenerAnalyticsLoading, setListenerAnalyticsLoading] = useState(false);
    const [isListenerAnalyticsModalVisible, setIsListenerAnalyticsModalVisible] = useState(false);

    // Post-registration org assignment state
    const [registeredListenerId, setRegisteredListenerId] = useState(null);
    const [registeredUserEmail, setRegisteredUserEmail] = useState('');
    const [postRegOrgId, setPostRegOrgId] = useState('');
    const [postRegRole, setPostRegRole] = useState('member');
    const [postRegAssigning, setPostRegAssigning] = useState(false);
    const [postRegAssignSuccess, setPostRegAssignSuccess] = useState('');
    const [postRegAssignError, setPostRegAssignError] = useState('');
    const [postRegOrgList, setPostRegOrgList] = useState([]);

    // Manage user modal state
    const [manageUserModalVisible, setManageUserModalVisible] = useState(false);
    const [managedListener, setManagedListener] = useState(null);
    const [managedDisplayName, setManagedDisplayName] = useState('');
    const [manageUserLoading, setManageUserLoading] = useState(false);
    const [manageUserMsg, setManageUserMsg] = useState('');
    const [manageUserError, setManageUserError] = useState('');

    const checkAdminAccess = useCallback(async () => {
        console.log('🔍 AdminPanel: Checking admin access...');
        try {
            const adminStatus = await AdminManager.checkAdminStatus();
            console.log('✅ AdminPanel: Admin status received:', adminStatus);
            setIsAdmin(adminStatus);

            if (!adminStatus) {
                console.log('❌ AdminPanel: Not admin - redirecting to home');
                navigate('/');
            } else {
                console.log('✅ AdminPanel: Admin access granted!');
            }
        } catch (error) {
            console.error('❌ AdminPanel: Error checking admin access:', error);
            navigate('/');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        checkAdminAccess();
    }, [checkAdminAccess]);

    // Email validation
    const isValidEmail = (email) => {
        const emailRegEx = /[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
        return emailRegEx.test(email);
    };

    // Password validation (at least 8 characters, one capital letter)
    const isValidPassword = (password) => {
        const passwordRegEx = /^(?=.*[A-Z]).{8,}$/;
        return passwordRegEx.test(password);
    };

    const handleRegisterFirebaseUser = async () => {
        const { name, email, password, confirmPassword } = newFirebaseUser;

        // Validation
        if (!name || !email || !password) {
            setFirebaseRegisterError(t('app.admin.error_fill_all_fields'));
            return;
        }

        if (!isValidEmail(email)) {
            setFirebaseRegisterError(t('app.admin.error_invalid_email'));
            return;
        }

        if (!isValidPassword(password)) {
            setFirebaseRegisterError(t('app.admin.error_password_requirements'));
            return;
        }

        if (password !== confirmPassword) {
            setFirebaseRegisterError(t('app.admin.error_passwords_no_match'));
            return;
        }

        setFirebaseRegisterLoading(true);
        setFirebaseRegisterError('');
        setFirebaseRegisterSuccess(false);

        try {
            const registeredEmail = newFirebaseUser.email;
            const result = await AdminManager.registerFirebaseUser(newFirebaseUser);

            if (result.success) {
                setFirebaseRegisterSuccess(true);
                setRegisteredUserEmail(registeredEmail);
                setRegisteredListenerId(null);
                setPostRegOrgId('');
                setPostRegRole('member');
                setPostRegAssignSuccess('');
                setPostRegAssignError('');
                // Load orgs for assignment dropdown
                OrgManager.adminListOrgs().then(data => setPostRegOrgList(data || [])).catch(() => {});
                // Clear form
                setNewFirebaseUser({
                    name: '',
                    email: '',
                    password: '',
                    confirmPassword: ''
                });
            } else {
                setFirebaseRegisterError(result.error || t('app.admin.error_register_user_failed'));
            }
        } catch (error) {
            setFirebaseRegisterError(error.message || t('app.admin.error_register_user'));
        } finally {
            setFirebaseRegisterLoading(false);
        }
    };

    const handleRegisterListener = async () => {
        if (!newListener.id || !newListener.username || !newListener.email) {
            setRegisterError(t('app.admin.error_fill_required_fields'));
            return;
        }

        setRegisterLoading(true);
        setRegisterError('');
        setRegisterSuccess(false);

        try {
            const result = await AdminManager.registerListener(newListener);

            if (result.success) {
                setRegisterSuccess(true);
                // Clear form
                setNewListener({
                    id: '',
                    username: '',
                    email: '',
                    general_request: ''
                });
                setTimeout(() => setRegisterSuccess(false), 3000);
            } else {
                setRegisterError(t('app.admin.error_register_listener_failed'));
            }
        } catch (error) {
            setRegisterError(error.response?.data?.detail || t('app.admin.error_register_listener'));
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleCreateSuggestedList = async () => {
        if (!newSuggestedList.list_name || !newSuggestedList.usernames) {
            setCreateListError('Please fill in all required fields');
            return;
        }

        setCreateListLoading(true);
        setCreateListError('');
        setCreateListSuccess(false);

        try {
            const usernamesArray = newSuggestedList.usernames
                .split(',')
                .map(u => u.trim())
                .filter(u => u.length > 0);

            const result = await AdminManager.generateSuggestedList({
                ...newSuggestedList,
                usernames: usernamesArray
            });

            if (result.status) {
                setCreateListSuccess(true);
                // Clear form
                setNewSuggestedList({
                    list_name: '',
                    list_prompt: '',
                    usernames: ''
                });
                // Refresh suggested lists
                loadSuggestedLists();
                setTimeout(() => setCreateListSuccess(false), 3000);
            } else {
                setCreateListError('Failed to create suggested list');
            }
        } catch (error) {
            setCreateListError(error.response?.data?.detail || 'Error creating suggested list');
        } finally {
            setCreateListLoading(false);
        }
    };

    const loadSuggestedLists = async () => {
        setSuggestedListsLoading(true);
        try {
            const lists = await AdminManager.getSuggestedLists(20, 0);
            setSuggestedLists(lists || []);
        } catch (error) {
            console.error('Error loading suggested lists:', error);
        } finally {
            setSuggestedListsLoading(false);
        }
    };

    const loadStats = async () => {
        setStatsLoading(true);
        try {
            const [msgStats, database] = await Promise.all([
                AdminManager.getMessageStats(60),
                AdminManager.getDatabaseStats()
            ]);
            setMessageStats(msgStats);
            setDbStats(database);
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const loadAvailableListsAndListeners = async () => {
        try {
            const [lists, listeners] = await Promise.all([
                AdminManager.getAllSuggestedLists(),
                AdminManager.getAllListeners(listenerSearchEmail)
            ]);
            setAvailableLists(lists || []);
            setAvailableListeners(listeners || []);
        } catch (error) {
            console.error('Error loading lists and listeners:', error);
        }
    };

    const loadListenersData = async () => {
        setListenersDataLoading(true);
        try {
            const data = await AdminManager.getListenersData();
            setListenersData(data || []);
        } catch (error) {
            console.error('Error loading listeners data:', error);
        } finally {
            setListenersDataLoading(false);
        }
    };

    const loadDetailedStats = async () => {
        setStatsLoading(true);
        try {
            const stats = await AdminManager.getComprehensiveStats();
            setDetailedStats(stats);

            // Also load the DB stats
            const database = await AdminManager.getDatabaseStats();
            setDbStats(database);
        } catch (error) {
            console.error('Error loading detailed stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const loadOrgs = async (search = '') => {
        setOrgsLoading(true);
        try {
            const data = await OrgManager.adminListOrgs(search || undefined);
            setOrgs(data || []);
        } catch (e) {
            console.error('Error loading orgs:', e);
        } finally {
            setOrgsLoading(false);
        }
    };

    const openListenerAnalytics = async (listenerId) => {
        setIsListenerAnalyticsModalVisible(true);
        setListenerAnalyticsLoading(true);
        setListenerAnalytics(null);
        try {
            const data = await AdminManager.getSingleListenerAnalytics(listenerId, 90);
            setListenerAnalytics(data);
        } catch (error) {
            console.error('Error loading listener analytics:', error);
        } finally {
            setListenerAnalyticsLoading(false);
        }
    };

    const loadAnalytics = async (days = analyticsPeriod) => {
        setAnalyticsLoading(true);
        try {
            const data = await AdminManager.getListenerAnalytics(days);
            setAnalyticsData(data);
        } catch (error) {
            console.error('Error loading analytics:', error);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const handleCreateOrg = async () => {
        if (!newOrg.name || !newOrg.slug) {
            setCreateOrgError('Name and slug are required');
            return;
        }
        setCreateOrgLoading(true);
        setCreateOrgError('');
        setCreateOrgSuccess('');
        try {
            const result = await OrgManager.adminCreateOrg(
                newOrg.name,
                newOrg.slug,
                newOrgOwner ? newOrgOwner.id : undefined,
                newOrg.plan
            );
            setCreateOrgSuccess(`Organization created! ID: ${result.id || result.org_id || JSON.stringify(result)}`);
            setNewOrg({ name: '', slug: '', plan: 'free' });
            setNewOrgOwner(null);
            setNewOrgOwnerSearch('');
            setNewOrgOwnerResults([]);
            loadOrgs(orgSearch);
        } catch (e) {
            setCreateOrgError(e.message || 'Failed to create organization');
        } finally {
            setCreateOrgLoading(false);
        }
    };

    const searchOrgOwner = async (query) => {
        setNewOrgOwnerSearch(query);
        if (query.length < 2) {
            setNewOrgOwnerResults([]);
            return;
        }
        try {
            const results = await AdminManager.getAllListeners(query);
            setNewOrgOwnerResults(results || []);
        } catch (e) {
            console.error('Error searching listeners:', e);
        }
    };

    const toggleExpandOrg = async (orgId) => {
        if (expandedOrgId === orgId) {
            setExpandedOrgId(null);
            setExpandedOrgDetail(null);
            return;
        }
        setExpandedOrgId(orgId);
        setExpandedOrgDetail(null);
        setExpandedOrgDetailLoading(true);
        try {
            const detail = await OrgManager.adminGetOrg(orgId);
            setExpandedOrgDetail(detail);
        } catch (e) {
            console.error('Error fetching org detail:', e);
        } finally {
            setExpandedOrgDetailLoading(false);
        }
    };

    const handleEditOrg = (org) => {
        setEditingOrgId(org.id);
        setEditingOrgData({ name: org.name, plan: org.plan, slug: org.slug });
    };

    const handleSaveEditOrg = async () => {
        setEditOrgLoading(true);
        try {
            await OrgManager.adminUpdateOrg(editingOrgId, editingOrgData);
            setEditingOrgId(null);
            loadOrgs(orgSearch);
        } catch (e) {
            console.error('Error updating org:', e);
        } finally {
            setEditOrgLoading(false);
        }
    };

    const handleDeleteOrg = async (orgId) => {
        setDeleteOrgLoading(true);
        try {
            await OrgManager.adminDeleteOrg(orgId);
            setDeleteOrgConfirmId(null);
            if (expandedOrgId === orgId) {
                setExpandedOrgId(null);
                setExpandedOrgDetail(null);
            }
            loadOrgs(orgSearch);
        } catch (e) {
            console.error('Error deleting org:', e);
        } finally {
            setDeleteOrgLoading(false);
        }
    };

    const searchAddMember = async (orgId, query) => {
        setAddMemberSearch(prev => ({ ...prev, [orgId]: query }));
        if (query.length < 2) {
            setAddMemberResults(prev => ({ ...prev, [orgId]: [] }));
            return;
        }
        try {
            const results = await AdminManager.getAllListeners(query);
            setAddMemberResults(prev => ({ ...prev, [orgId]: results || [] }));
        } catch (e) {
            console.error('Error searching for member:', e);
        }
    };

    const handleAddMember = async (orgId, listener) => {
        const role = addMemberRole[orgId] || 'member';
        setAddMemberLoading(prev => ({ ...prev, [orgId]: true }));
        try {
            await OrgManager.adminAddMember(orgId, listener.id, role);
            // Refresh expanded org detail
            const detail = await OrgManager.adminGetOrg(orgId);
            setExpandedOrgDetail(detail);
            setAddMemberSearch(prev => ({ ...prev, [orgId]: '' }));
            setAddMemberResults(prev => ({ ...prev, [orgId]: [] }));
        } catch (e) {
            console.error('Error adding member:', e);
        } finally {
            setAddMemberLoading(prev => ({ ...prev, [orgId]: false }));
        }
    };

    const handleRemoveMember = async (orgId, listenerId) => {
        try {
            await OrgManager.adminRemoveMember(orgId, listenerId);
            const detail = await OrgManager.adminGetOrg(orgId);
            setExpandedOrgDetail(detail);
        } catch (e) {
            console.error('Error removing member:', e);
        }
    };

    const handleChangeMemberRole = async (orgId, listenerId, role) => {
        try {
            await OrgManager.adminChangeMemberRole(orgId, listenerId, role);
            const detail = await OrgManager.adminGetOrg(orgId);
            setExpandedOrgDetail(detail);
        } catch (e) {
            console.error('Error changing member role:', e);
        }
    };

    const handlePostRegAssign = async () => {
        if (!postRegOrgId) {
            setPostRegAssignError('Please select an organization');
            return;
        }
        setPostRegAssigning(true);
        setPostRegAssignError('');
        setPostRegAssignSuccess('');
        try {
            // Find the listener by email
            const listeners = await AdminManager.getAllListeners(registeredUserEmail);
            if (!listeners || listeners.length === 0) {
                setPostRegAssignError('Could not find the registered user in listener list. Try again in a moment.');
                return;
            }
            const listener = listeners[0];
            await OrgManager.adminBulkAddListenerToOrgs(listener.id, [postRegOrgId], postRegRole);
            setPostRegAssignSuccess(`Successfully assigned to organization!`);
            setRegisteredListenerId(listener.id);
        } catch (e) {
            setPostRegAssignError(e.message || 'Failed to assign user to organization');
        } finally {
            setPostRegAssigning(false);
        }
    };

    const openActivityModal = async (listener) => {
        setSelectedListenerName(listener.email || listener.username);
        setIsActivityModalVisible(true);
        setActivityLoading(true);
        setSelectedListenerActivity(null);
        try {
            const activity = await AdminManager.getListenerActivity(listener.id);
            setSelectedListenerActivity(activity);
        } catch (error) {
            console.error('Failed to load activity for listener:', error);
            setSelectedListenerActivity([]);
        } finally {
            setActivityLoading(false);
        }
    };

    const openManageUserModal = (listener) => {
        setManagedListener(listener);
        setManagedDisplayName('');
        setManageUserMsg('');
        setManageUserError('');
        setManageUserModalVisible(true);
    };

    const handleUpdateDisplayName = async () => {
        if (!managedDisplayName.trim()) return;
        setManageUserLoading(true);
        setManageUserMsg('');
        setManageUserError('');
        try {
            await AdminManager.updateListenerProfile(managedListener.id, { displayName: managedDisplayName.trim() });
            setManageUserMsg(`Display name updated to "${managedDisplayName.trim()}"`);
            setManagedDisplayName('');
        } catch (error) {
            setManageUserError(error.response?.data?.detail || 'Failed to update display name');
        } finally {
            setManageUserLoading(false);
        }
    };

    const handleSendPasswordReset = async () => {
        setManageUserLoading(true);
        setManageUserMsg('');
        setManageUserError('');
        try {
            const result = await AdminManager.sendPasswordReset(managedListener.id);
            setManageUserMsg(`Password reset email sent to ${result.email}`);
        } catch (error) {
            setManageUserError(error.response?.data?.detail || 'Failed to send password reset');
        } finally {
            setManageUserLoading(false);
        }
    };

    const handleAssignLists = async () => {
        if (selectedListIds.length === 0) {
            setAssignError('Please select at least one list');
            return;
        }
        if (selectedListenerIds.length === 0) {
            setAssignError('Please select at least one user');
            return;
        }

        setAssignLoading(true);
        setAssignError('');
        setAssignSuccess(false);
        setAssignResult(null);

        try {
            const result = await AdminManager.assignSuggestedListsToUsers(
                selectedListIds,
                selectedListenerIds
            );

            setAssignResult(result);
            if (result.success) {
                setAssignSuccess(true);
                // Clear selections
                setSelectedListIds([]);
                setSelectedListenerIds([]);
                setTimeout(() => {
                    setAssignSuccess(false);
                    setAssignResult(null);
                }, 5000);
            } else {
                setAssignError('Failed to assign lists to users');
            }
        } catch (error) {
            setAssignError(error.message || 'Error assigning lists');
        } finally {
            setAssignLoading(false);
        }
    };

    const toggleListSelection = (listId) => {
        console.log('🔘 Toggling list selection:', listId);
        setSelectedListIds(prev => {
            const newSelection = prev.includes(listId)
                ? prev.filter(id => id !== listId)
                : [...prev, listId];
            console.log('📋 Selected list IDs:', newSelection);
            return newSelection;
        });
    };

    const toggleListenerSelection = (listenerId) => {
        setSelectedListenerIds(prev =>
            prev.includes(listenerId)
                ? prev.filter(id => id !== listenerId)
                : [...prev, listenerId]
        );
    };

    useEffect(() => {
        if (activeTab === 'suggested_lists' && suggestedLists.length === 0) {
            loadSuggestedLists();
        } else if (activeTab === 'stats') {
            loadDetailedStats();
        } else if (activeTab === 'assign_lists') {
            loadAvailableListsAndListeners();
        } else if (activeTab === 'listeners_data') {
            loadListenersData();
        } else if (activeTab === 'organizations') {
            loadOrgs();
        } else if (activeTab === 'analytics') {
            loadAnalytics();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    if (loading) {
        return (
            <View style={styles.container}>
                <ActivityIndicator size="large" color="#4DA6FF" />
            </View>
        );
    }

    if (!isAdmin) {
        return null; // Will redirect
    }

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                .ap-tab { cursor: pointer; transition: background 0.12s; }
                .ap-tab:hover { background: ${THEME.bg} !important; }
                ::-webkit-scrollbar { width: 4px; height: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
            ` }} />
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: THEME.bg, overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 24px', borderBottom: `1px solid ${THEME.border}`, flexShrink: 0, background: THEME.bgCard }}>
                    <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#4DA6FF', fontSize: 13, fontWeight: 600 }}>
                        <FaArrowLeft size={12} color="#4DA6FF" />
                        <span>{t('app.admin.back')}</span>
                    </div>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(77,166,255,0.12)', border: '1px solid rgba(77,166,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaDatabase size={16} color="#4DA6FF" />
                    </div>
                    <div>
                        <div style={{ color: THEME.textPrimary, fontSize: 20, fontWeight: 700, letterSpacing: -0.3 }}>{t('app.admin.title')}</div>
                        <div style={{ color: THEME.textMuted, fontSize: 12, marginTop: 1 }}>{t('app.admin.subtitle')}</div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                    {/* Left sidebar with vertical tab list */}
                    <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: THEME.bgCard }}>
                        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                            <div
                                className="ap-tab"
                                onClick={() => navigate('/daily-review')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 12px', borderRadius: 8, marginBottom: 8,
                                    background: 'rgba(22,163,74,0.1)',
                                    border: '1px solid rgba(22,163,74,0.3)',
                                    cursor: 'pointer',
                                }}
                            >
                                <div style={{
                                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                    background: 'rgba(22,163,74,0.2)',
                                    border: '1px solid rgba(22,163,74,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <FaFire size={12} color="#16a34a" />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>
                                    Daily Reports
                                </span>
                            </div>
                            {[
                                { key: 'register',        icon: FaUserPlus,  label: t('app.admin.tab_register') },
                                { key: 'listeners',       icon: FaUserPlus,  label: t('app.admin.tab_add_listener') },
                                { key: 'suggested_lists', icon: FaList,      label: t('app.admin.tab_suggested_lists') },
                                { key: 'assign_lists',    icon: FaList,      label: t('app.admin.tab_assign_lists') },
                                { key: 'analytics',       icon: FaUsers,     label: t('app.admin.tab_analytics') },
                                { key: 'stats',           icon: FaChartBar,  label: t('app.admin.tab_statistics') },
                                { key: 'listeners_data',  icon: FaDatabase,  label: t('app.admin.tab_listeners_data') },
                                { key: 'organizations',   icon: FaBuilding,  label: t('app.admin.tab_organizations') },
                                { key: 'article_analyzer', icon: FaFire,      label: 'Article Analyzer' },
                                { key: 'product_detection', icon: FaCamera,   label: 'Product Detection' },
                                { key: 'demo_generator',  icon: FaMagic,     label: 'Demo Generator' },
                            ].map(({ key, icon: Icon, label }) => (
                                <div
                                    key={key}
                                    className="ap-tab"
                                    onClick={() => setActiveTab(key)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '10px 12px', borderRadius: 8, marginBottom: 2,
                                        background: activeTab === key ? 'rgba(77,166,255,0.1)' : 'transparent',
                                    }}
                                >
                                    <div style={{
                                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                        background: activeTab === key ? 'rgba(77,166,255,0.2)' : THEME.bg,
                                        border: `1px solid ${activeTab === key ? 'rgba(77,166,255,0.3)' : THEME.border}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon size={12} color={activeTab === key ? '#4DA6FF' : THEME.textMuted} />
                                    </div>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: activeTab === key ? '#4DA6FF' : THEME.textMuted }}>
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right content area */}
                    <ScrollView style={styles.content}>
                {activeTab === 'register' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{t('app.admin.register_new_user')}</Text>
                        <Text style={styles.sectionSubtitle}>
                            {t('app.admin.register_new_user_subtitle')}
                        </Text>
                        <View style={[styles.infoBox, { marginBottom: 16 }]}>
                            <Text style={styles.infoText}>Note: After registration, the user will need to be assigned to an organization before they can use the platform.</Text>
                        </View>

                        <View style={styles.form}>
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.full_name')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.full_name_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newFirebaseUser.name}
                                    onChangeText={(text) => setNewFirebaseUser({ ...newFirebaseUser, name: text })}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.email_address')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.email_address_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newFirebaseUser.email}
                                    onChangeText={(text) => setNewFirebaseUser({ ...newFirebaseUser, email: text })}
                                    keyboardType="email-address"
                                    autoCapitalize="none"
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.password')} *</Text>
                                <Text style={styles.helperText}>
                                    {t('app.admin.password_helper')}
                                </Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.password_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newFirebaseUser.password}
                                    onChangeText={(text) => setNewFirebaseUser({ ...newFirebaseUser, password: text })}
                                    secureTextEntry
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.confirm_password')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.confirm_password_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newFirebaseUser.confirmPassword}
                                    onChangeText={(text) => setNewFirebaseUser({ ...newFirebaseUser, confirmPassword: text })}
                                    secureTextEntry
                                />
                            </View>

                            {firebaseRegisterError && (
                                <View style={styles.errorContainer}>
                                    <FaTimes size={16} color="#ff4444" />
                                    <Text style={styles.errorText}>{firebaseRegisterError}</Text>
                                </View>
                            )}

                            {firebaseRegisterSuccess && (
                                <View style={styles.successContainer}>
                                    <FaCheck size={16} color="#44ff44" />
                                    <Text style={styles.successText}>
                                        {t('app.admin.user_registered_success')}
                                    </Text>
                                </View>
                            )}

                            <TouchableOpacity
                                style={[styles.submitButton, firebaseRegisterLoading && styles.submitButtonDisabled]}
                                onPress={handleRegisterFirebaseUser}
                                disabled={firebaseRegisterLoading}
                            >
                                {firebaseRegisterLoading ? (
                                    <ActivityIndicator size="small" color="#fff" />
                                ) : (
                                    <>
                                        <FaUserPlus size={18} color="#fff" />
                                        <Text style={styles.submitButtonText}>{t('app.admin.create_user_account')}</Text>
                                    </>
                                )}
                            </TouchableOpacity>
                        </View>

                        {/* Post-registration org assignment */}
                        {firebaseRegisterSuccess && registeredUserEmail && (
                            <View style={[styles.form, { borderWidth: 1, borderColor: 'rgba(77,166,255,0.4)' }]}>
                                <Text style={[styles.sectionTitle, { fontSize: 18, marginBottom: 4 }]}>{t('app.admin.assign_to_org_optional')}</Text>
                                <Text style={styles.helperText}>{t('app.admin.assign_org_helper', { email: registeredUserEmail })}</Text>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>{t('app.admin.select_organization')}</Text>
                                    {postRegOrgList.length === 0 ? (
                                        <Text style={styles.helperText}>No organizations found. Create one in the Organizations tab first.</Text>
                                    ) : (
                                        <select
                                            value={postRegOrgId}
                                            onChange={e => setPostRegOrgId(e.target.value)}
                                            style={{
                                                backgroundColor: THEME.bg,
                                                color: THEME.textPrimary,
                                                border: `1px solid ${THEME.border}`,
                                                borderRadius: 8,
                                                padding: '10px 12px',
                                                fontSize: 14,
                                                width: '100%',
                                                marginBottom: 4,
                                            }}
                                        >
                                            <option value="">-- Select an organization --</option>
                                            {postRegOrgList.map(org => (
                                                <option key={org.id} value={org.id}>{org.name} ({org.slug})</option>
                                            ))}
                                        </select>
                                    )}
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>{t('app.admin.role')}</Text>
                                    <select
                                        value={postRegRole}
                                        onChange={e => setPostRegRole(e.target.value)}
                                        style={{
                                            backgroundColor: THEME.bg,
                                            color: THEME.textPrimary,
                                            border: `1px solid ${THEME.border}`,
                                            borderRadius: 8,
                                            padding: '10px 12px',
                                            fontSize: 14,
                                            width: '100%',
                                        }}
                                    >
                                        <option value="owner">{t('app.admin.role_owner')}</option>
                                        <option value="admin">{t('app.admin.role_admin')}</option>
                                        <option value="member">{t('app.admin.role_member')}</option>
                                        <option value="viewer">{t('app.admin.role_viewer')}</option>
                                    </select>
                                </View>

                                {postRegAssignError ? (
                                    <View style={styles.errorContainer}>
                                        <FaTimes size={16} color="#ff4444" />
                                        <Text style={styles.errorText}>{postRegAssignError}</Text>
                                    </View>
                                ) : null}

                                {postRegAssignSuccess ? (
                                    <View style={styles.successContainer}>
                                        <FaCheck size={16} color="#44ff44" />
                                        <Text style={styles.successText}>{postRegAssignSuccess}</Text>
                                    </View>
                                ) : null}

                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                    <TouchableOpacity
                                        style={[styles.submitButton, { flex: 1 }, postRegAssigning && styles.submitButtonDisabled]}
                                        onPress={handlePostRegAssign}
                                        disabled={postRegAssigning || !!postRegAssignSuccess}
                                    >
                                        {postRegAssigning ? (
                                            <ActivityIndicator size="small" color="#fff" />
                                        ) : (
                                            <>
                                                <FaBuilding size={16} color="#fff" />
                                                <Text style={styles.submitButtonText}>{t('app.admin.assign_to_org')}</Text>
                                            </>
                                        )}
                                    </TouchableOpacity>

                                    <TouchableOpacity
                                        style={[styles.submitButton, { flex: 1, backgroundColor: 'rgba(255,255,255,0.1)' }]}
                                        onPress={() => {
                                            setFirebaseRegisterSuccess(false);
                                            setRegisteredUserEmail('');
                                            setPostRegOrgList([]);
                                        }}
                                    >
                                        <Text style={styles.submitButtonText}>{t('app.admin.skip_for_now')}</Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        )}

                        <View style={styles.infoBox}>
                            <Text style={styles.infoTitle}>ℹ️ What happens when you register a user:</Text>
                            <Text style={styles.infoText}>1. Creates a Firebase Authentication account</Text>
                            <Text style={styles.infoText}>2. Automatically adds them as a listener in the database</Text>
                            <Text style={styles.infoText}>3. User can immediately log in with their credentials</Text>
                        </View>
                    </View>
                )}

                {activeTab === 'listeners' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{t('app.admin.add_existing_listener')}</Text>
                        <Text style={styles.sectionSubtitle}>
                            {t('app.admin.add_existing_listener_subtitle')}
                        </Text>

                        <View style={styles.form}>
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.firebase_uid')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.firebase_uid_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newListener.id}
                                    onChangeText={(text) => setNewListener({ ...newListener, id: text })}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.username')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.username_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newListener.username}
                                    onChangeText={(text) => setNewListener({ ...newListener, username: text })}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.email')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.email_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newListener.email}
                                    onChangeText={(text) => setNewListener({ ...newListener, email: text })}
                                    keyboardType="email-address"
                                    autoCapitalize="none"
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.general_request')}</Text>
                                <TextInput
                                    style={[styles.input, styles.textArea]}
                                    placeholder={t('app.admin.general_request_placeholder')}
                                    placeholderTextColor="#666"
                                    value={newListener.general_request}
                                    onChangeText={(text) => setNewListener({ ...newListener, general_request: text })}
                                    multiline
                                    numberOfLines={3}
                                />
                            </View>

                            {registerError && (
                                <View style={styles.errorContainer}>
                                    <FaTimes size={16} color="#ff4444" />
                                    <Text style={styles.errorText}>{registerError}</Text>
                                </View>
                            )}

                            {registerSuccess && (
                                <View style={styles.successContainer}>
                                    <FaCheck size={16} color="#44ff44" />
                                    <Text style={styles.successText}>{t('app.admin.listener_registered_success')}</Text>
                                </View>
                            )}

                            <TouchableOpacity
                                style={[styles.submitButton, registerLoading && styles.submitButtonDisabled]}
                                onPress={handleRegisterListener}
                                disabled={registerLoading}
                            >
                                {registerLoading ? (
                                    <ActivityIndicator size="small" color="#fff" />
                                ) : (
                                    <>
                                        <FaUserPlus size={18} color="#fff" />
                                        <Text style={styles.submitButtonText}>{t('app.admin.register_listener')}</Text>
                                    </>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                )}

                {activeTab === 'suggested_lists' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{t('app.admin.createSuggestedList')}</Text>
                        <Text style={styles.sectionSubtitle}>
                            {t('app.admin.createSuggestedListSubtitle')}
                        </Text>

                        <View style={styles.form}>
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.listName')} *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder={t('app.admin.listNamePlaceholder')}
                                    placeholderTextColor="#666"
                                    value={newSuggestedList.list_name}
                                    onChangeText={(text) => setNewSuggestedList({ ...newSuggestedList, list_name: text })}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.listPrompt')}</Text>
                                <TextInput
                                    style={[styles.input, styles.textArea]}
                                    placeholder={t('app.admin.listPromptPlaceholder')}
                                    placeholderTextColor="#666"
                                    value={newSuggestedList.list_prompt}
                                    onChangeText={(text) => setNewSuggestedList({ ...newSuggestedList, list_prompt: text })}
                                    multiline
                                    numberOfLines={3}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>{t('app.admin.usernames')} *</Text>
                                <Text style={styles.helperText}>{t('app.admin.usernamesHelper')}</Text>
                                <TextInput
                                    style={[styles.input, styles.textArea]}
                                    placeholder={t('app.admin.usernamesPlaceholder')}
                                    placeholderTextColor="#666"
                                    value={newSuggestedList.usernames}
                                    onChangeText={(text) => setNewSuggestedList({ ...newSuggestedList, usernames: text })}
                                    multiline
                                    numberOfLines={4}
                                />
                            </View>

                            {createListError && (
                                <View style={styles.errorContainer}>
                                    <FaTimes size={16} color="#ff4444" />
                                    <Text style={styles.errorText}>{createListError}</Text>
                                </View>
                            )}

                            {createListSuccess && (
                                <View style={styles.successContainer}>
                                    <FaCheck size={16} color="#44ff44" />
                                    <Text style={styles.successText}>{t('app.admin.suggestedListCreatedSuccess')}</Text>
                                </View>
                            )}

                            <TouchableOpacity
                                style={[styles.submitButton, createListLoading && styles.submitButtonDisabled]}
                                onPress={handleCreateSuggestedList}
                                disabled={createListLoading}
                            >
                                {createListLoading ? (
                                    <ActivityIndicator size="small" color="#fff" />
                                ) : (
                                    <>
                                        <FaList size={18} color="#fff" />
                                        <Text style={styles.submitButtonText}>{t('app.admin.createSuggestedList')}</Text>
                                    </>
                                )}
                            </TouchableOpacity>
                        </View>

                        {/* Existing Suggested Lists */}
                        <View style={styles.listsContainer}>
                            <Text style={styles.sectionTitle}>{t('app.admin.existingSuggestedLists')}</Text>
                            {suggestedListsLoading ? (
                                <ActivityIndicator size="large" color="#4DA6FF" style={{ marginTop: 20 }} />
                            ) : suggestedLists.length > 0 ? (
                                suggestedLists.map((list, index) => (
                                    <View key={index} style={styles.listCard}>
                                        <Text style={styles.listCardTitle}>{list.list_name}</Text>
                                        {list.list_prompt && (
                                            <Text style={styles.listCardSubtitle}>{list.list_prompt}</Text>
                                        )}
                                        <Text style={styles.listCardDate}>
                                            Created: {new Date(list.created_at).toLocaleDateString()}
                                        </Text>
                                    </View>
                                ))
                            ) : (
                                <Text style={styles.emptyText}>No suggested lists found</Text>
                            )}
                        </View>
                    </View>
                )}

                {activeTab === 'assign_lists' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{t('app.admin.assignListsToUsers')}</Text>
                        <Text style={styles.sectionSubtitle}>
                            {t('app.admin.assignListsSubtitle')}
                        </Text>

                        <View style={styles.assignContainer}>
                            {/* Lists Selection */}
                            <View style={styles.assignColumn}>
                                <Text style={styles.assignColumnTitle}>📋 Select Lists ({selectedListIds.length} selected)</Text>
                                <ScrollView style={styles.selectionList}>
                                    {availableLists.map((list) => (
                                        <TouchableOpacity
                                            key={list.id}
                                            style={[
                                                styles.selectionItem,
                                                selectedListIds.includes(list.id) && styles.selectionItemSelected
                                            ]}
                                            onPress={() => toggleListSelection(list.id)}
                                        >
                                            <View style={styles.checkbox}>
                                                {selectedListIds.includes(list.id) && (
                                                    <FaCheck size={12} color="#4DA6FF" />
                                                )}
                                            </View>
                                            <View style={styles.selectionItemContent}>
                                                <Text style={styles.selectionItemTitle}>{list.list_name}</Text>
                                                {list.list_prompt && (
                                                    <Text style={styles.selectionItemSubtitle}>{list.list_prompt}</Text>
                                                )}
                                            </View>
                                        </TouchableOpacity>
                                    ))}
                                    {availableLists.length === 0 && (
                                        <Text style={styles.emptyText}>No suggested lists available</Text>
                                    )}
                                </ScrollView>
                            </View>

                            {/* Users Selection */}
                            <View style={styles.assignColumn}>
                                <Text style={styles.assignColumnTitle}>👥 Select Users ({selectedListenerIds.length} selected)</Text>

                                <TextInput
                                    style={styles.searchInput}
                                    placeholder="Search by email..."
                                    placeholderTextColor="#666"
                                    value={listenerSearchEmail}
                                    onChangeText={(text) => {
                                        setListenerSearchEmail(text);
                                        // Reload listeners with search
                                        setTimeout(() => loadAvailableListsAndListeners(), 300);
                                    }}
                                />

                                <ScrollView style={styles.selectionList}>
                                    {availableListeners.map((listener) => (
                                        <TouchableOpacity
                                            key={listener.id}
                                            style={[
                                                styles.selectionItem,
                                                selectedListenerIds.includes(listener.id) && styles.selectionItemSelected
                                            ]}
                                            onPress={() => toggleListenerSelection(listener.id)}
                                        >
                                            <View style={styles.checkbox}>
                                                {selectedListenerIds.includes(listener.id) && (
                                                    <FaCheck size={12} color="#4DA6FF" />
                                                )}
                                            </View>
                                            <View style={styles.selectionItemContent}>
                                                <Text style={styles.selectionItemTitle}>{listener.email}</Text>
                                                {listener.username && listener.username !== listener.email && (
                                                    <Text style={styles.selectionItemSubtitle}>{listener.username}</Text>
                                                )}
                                            </View>
                                        </TouchableOpacity>
                                    ))}
                                    {availableListeners.length === 0 && (
                                        <Text style={styles.emptyText}>No users found</Text>
                                    )}
                                </ScrollView>
                            </View>
                        </View>

                        {/* Assignment Results */}
                        {assignError && (
                            <View style={styles.errorContainer}>
                                <FaTimes size={16} color="#ff4444" />
                                <Text style={styles.errorText}>{assignError}</Text>
                            </View>
                        )}

                        {assignSuccess && assignResult && (
                            <View style={styles.successContainer}>
                                <FaCheck size={16} color="#44ff44" />
                                <Text style={styles.successText}>
                                    Successfully assigned {assignResult.successful} out of {assignResult.total} list-user combinations!
                                </Text>
                            </View>
                        )}

                        {/* Assign Button */}
                        <TouchableOpacity
                            style={[styles.submitButton, assignLoading && styles.submitButtonDisabled]}
                            onPress={handleAssignLists}
                            disabled={assignLoading || selectedListIds.length === 0 || selectedListenerIds.length === 0}
                        >
                            {assignLoading ? (
                                <ActivityIndicator size="small" color="#fff" />
                            ) : (
                                <>
                                    <FaList size={18} color="#fff" />
                                    <Text style={styles.submitButtonText}>
                                        Assign {selectedListIds.length} List(s) to {selectedListenerIds.length} User(s)
                                    </Text>
                                </>
                            )}
                        </TouchableOpacity>

                        <View style={styles.infoBox}>
                            <Text style={styles.infoTitle}>ℹ️ How to assign lists:</Text>
                            <Text style={styles.infoText}>1. Select one or more suggested lists from the left column</Text>
                            <Text style={styles.infoText}>2. Select one or more users from the right column</Text>
                            <Text style={styles.infoText}>3. Click "Assign" to create all combinations</Text>
                            <Text style={styles.infoText}>4. Users will see these lists in their suggested lists</Text>
                        </View>
                    </View>
                )}


                {activeTab === 'analytics' && (
                    <View style={styles.section}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Text style={styles.sectionTitle}>{t('app.admin.userAnalytics')}</Text>
                            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                {[7, 14, 30, 90].map(d => (
                                    <div
                                        key={d}
                                        onClick={() => { setAnalyticsPeriod(d); loadAnalytics(d); }}
                                        style={{
                                            padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                                            background: analyticsPeriod === d ? 'rgba(77,166,255,0.2)' : THEME.bg,
                                            border: `1px solid ${analyticsPeriod === d ? 'rgba(77,166,255,0.4)' : THEME.border}`,
                                            color: analyticsPeriod === d ? '#4DA6FF' : THEME.textMuted,
                                            fontSize: 13, fontWeight: 600,
                                        }}
                                    >
                                        {d}d
                                    </div>
                                ))}
                            </View>
                        </View>
                        <Text style={styles.sectionSubtitle}>
                            Engagement metrics, feature usage, and activity trends across all users.
                        </Text>

                        {analyticsLoading ? (
                            <ActivityIndicator size="large" color="#4DA6FF" style={{ marginTop: 40 }} />
                        ) : analyticsData ? (
                            <View style={{ gap: 20 }}>
                                {/* Summary Cards */}
                                <View style={styles.statsRow}>
                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>{t('app.admin.userEngagement')}</Text>
                                        <View style={styles.statsGrid}>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{analyticsData.summary.total_listeners}</Text>
                                                <Text style={styles.statLabel}>{t('app.admin.totalUsers')}</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={[styles.statValue, { color: '#44ff44' }]}>{analyticsData.summary.active_7d}</Text>
                                                <Text style={styles.statLabel}>Active (7d)</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={[styles.statValue, { color: '#FFD700' }]}>{analyticsData.summary.active_24h}</Text>
                                                <Text style={styles.statLabel}>Active (24h)</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{analyticsData.summary.total_actions}</Text>
                                                <Text style={styles.statLabel}>Total Actions ({analyticsPeriod}d)</Text>
                                            </View>
                                        </View>
                                    </View>

                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>{t('app.admin.engagementRate')}</Text>
                                        <View style={styles.statsGrid}>
                                            <View style={styles.statItem}>
                                                <Text style={[styles.statValue, {
                                                    color: analyticsData.summary.total_listeners > 0
                                                        ? (analyticsData.summary.active_7d / analyticsData.summary.total_listeners > 0.5 ? '#44ff44' : '#ffaa00')
                                                        : THEME.textMuted
                                                }]}>
                                                    {analyticsData.summary.total_listeners > 0
                                                        ? Math.round((analyticsData.summary.active_7d / analyticsData.summary.total_listeners) * 100)
                                                        : 0}%
                                                </Text>
                                                <Text style={styles.statLabel}>{t('app.admin.weeklyActiveRate')}</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>
                                                    {analyticsData.summary.active_7d > 0
                                                        ? (analyticsData.summary.total_actions / analyticsData.summary.active_7d).toFixed(1)
                                                        : '0'}
                                                </Text>
                                                <Text style={styles.statLabel}>Avg Actions/User</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={[styles.statValue, { color: '#ff6b6b' }]}>{analyticsData.inactive_users.length}</Text>
                                                <Text style={styles.statLabel}>Inactive (14d+)</Text>
                                            </View>
                                        </View>
                                    </View>
                                </View>

                                {/* Daily Activity Trend */}
                                {analyticsData.daily_trend.length > 0 && (
                                    <View style={styles.statsCard}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 16 }]}>{t('app.admin.dailyActivityTrend')}</Text>
                                        <View style={{ height: 140, flexDirection: 'row', alignItems: 'flex-end', gap: 2, paddingBottom: 24 }}>
                                            {(() => {
                                                const maxActions = Math.max(...analyticsData.daily_trend.map(d => d.actions), 1);
                                                const barWidth = Math.max(4, Math.min(20, Math.floor(700 / analyticsData.daily_trend.length) - 2));
                                                return analyticsData.daily_trend.map((day, i) => {
                                                    const height = Math.max(2, (day.actions / maxActions) * 110);
                                                    const isToday = i === analyticsData.daily_trend.length - 1;
                                                    return (
                                                        <div
                                                            key={day.day}
                                                            title={`${day.day}\n${day.actions} actions\n${day.active_users} users`}
                                                            style={{
                                                                width: barWidth,
                                                                height,
                                                                borderRadius: 3,
                                                                background: isToday
                                                                    ? '#4DA6FF'
                                                                    : `rgba(77,166,255,${0.2 + (day.actions / maxActions) * 0.6})`,
                                                                cursor: 'pointer',
                                                                position: 'relative',
                                                            }}
                                                        />
                                                    );
                                                });
                                            })()}
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                            <Text style={{ color: THEME.textMuted, fontSize: 11 }}>
                                                {analyticsData.daily_trend[0]?.day}
                                            </Text>
                                            <Text style={{ color: THEME.textMuted, fontSize: 11 }}>
                                                {analyticsData.daily_trend[analyticsData.daily_trend.length - 1]?.day}
                                            </Text>
                                        </View>
                                    </View>
                                )}

                                {/* Feature Usage Breakdown */}
                                <View style={styles.statsRow}>
                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>{t('app.admin.featureUsage')}</Text>
                                        <View style={{ gap: 8 }}>
                                            {analyticsData.feature_usage.map((feature) => {
                                                const maxCount = analyticsData.feature_usage[0]?.total_count || 1;
                                                const pct = (feature.total_count / maxCount) * 100;
                                                const label = feature.action_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                                return (
                                                    <View key={feature.action_type} style={{ gap: 4 }}>
                                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Text style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '600' }}>{label}</Text>
                                                            <View style={{ flexDirection: 'row', gap: 12 }}>
                                                                <Text style={{ color: THEME.textMuted, fontSize: 12 }}>{feature.unique_users} users</Text>
                                                                <Text style={{ color: '#4DA6FF', fontSize: 12, fontWeight: '700' }}>{feature.total_count}</Text>
                                                            </View>
                                                        </View>
                                                        <View style={{ height: 6, borderRadius: 3, backgroundColor: THEME.bg, overflow: 'hidden' }}>
                                                            <View style={{ width: `${pct}%`, height: '100%', borderRadius: 3, backgroundColor: '#4DA6FF' }} />
                                                        </View>
                                                    </View>
                                                );
                                            })}
                                            {analyticsData.feature_usage.length === 0 && (
                                                <Text style={styles.emptyText}>{t('app.admin.noActivityData')}</Text>
                                            )}
                                        </View>
                                    </View>
                                </View>

                                {/* Per-User Table */}
                                <View style={styles.statsCard}>
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <Text style={styles.statsCardTitle}>Per-User Breakdown</Text>
                                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                            <TextInput
                                                style={[styles.searchInput, { marginBottom: 0, width: 200 }]}
                                                placeholder="Search email..."
                                                placeholderTextColor="#666"
                                                value={analyticsSearch}
                                                onChangeText={setAnalyticsSearch}
                                            />
                                            <select
                                                value={analyticsSort}
                                                onChange={e => setAnalyticsSort(e.target.value)}
                                                style={{
                                                    backgroundColor: THEME.bg, color: THEME.textPrimary,
                                                    border: `1px solid ${THEME.border}`, borderRadius: 8,
                                                    padding: '8px 12px', fontSize: 13,
                                                }}
                                            >
                                                <option value="actions_7d">Most Active (7d)</option>
                                                <option value="total_actions">Most Active (Total)</option>
                                                <option value="active_days">{t('app.admin.sortMostDaysActive')}</option>
                                                <option value="unique_features_used">{t('app.admin.sortMostFeaturesUsed')}</option>
                                                <option value="lists_count">{t('app.admin.sortMostLists')}</option>
                                                <option value="follows_count">{t('app.admin.sortMostFollows')}</option>
                                            </select>
                                        </View>
                                    </View>

                                    <ScrollView horizontal>
                                        <View style={{ minWidth: '100%' }}>
                                            <View style={styles.tableHeader}>
                                                <Text style={[styles.tableHeaderText, { width: 220 }]}>{t('app.admin.colUser')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 70 }]}>24h</Text>
                                                <Text style={[styles.tableHeaderText, { width: 70 }]}>7d</Text>
                                                <Text style={[styles.tableHeaderText, { width: 80 }]}>{t('app.admin.colTotal')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 80 }]}>{t('app.admin.colDays')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 80 }]}>{t('app.admin.colFeatures')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 70 }]}>{t('app.admin.colLists')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 80 }]}>{t('app.admin.colFollows')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 80 }]}>{t('app.admin.colStatus')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 140 }]}>{t('app.admin.colLastActive')}</Text>
                                                <Text style={[styles.tableHeaderText, { width: 90 }]}>{t('app.admin.colActions')}</Text>
                                            </View>
                                            {analyticsData.listeners
                                                .filter(l => !analyticsSearch || l.email?.toLowerCase().includes(analyticsSearch.toLowerCase()) || l.username?.toLowerCase().includes(analyticsSearch.toLowerCase()))
                                                .sort((a, b) => (b[analyticsSort] || 0) - (a[analyticsSort] || 0))
                                                .map((listener) => {
                                                    const isHot = listener.actions_7d >= 10;
                                                    const isCold = listener.actions_7d === 0;
                                                    return (
                                                        <View key={listener.listener_id} style={[styles.tableRow, isCold && { opacity: 0.6 }]}>
                                                            <View style={{ width: 220 }}>
                                                                <Text style={styles.tableCellTextBold}>{listener.email}</Text>
                                                                <Text style={styles.tableCellTextIds}>{listener.username}</Text>
                                                            </View>
                                                            <Text style={[styles.tableCellText, { width: 70, color: listener.actions_24h > 0 ? '#44ff44' : THEME.textMuted }]}>
                                                                {listener.actions_24h}
                                                            </Text>
                                                            <Text style={[styles.tableCellText, { width: 70, fontWeight: '700', color: isHot ? '#FFD700' : THEME.textPrimary }]}>
                                                                {listener.actions_7d}
                                                            </Text>
                                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.total_actions}</Text>
                                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.active_days}</Text>
                                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.unique_features_used}</Text>
                                                            <Text style={[styles.tableCellText, { width: 70 }]}>{listener.lists_count}</Text>
                                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.follows_count}</Text>
                                                            <View style={{ width: 80, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                                {isHot ? (
                                                                    <><FaFire size={12} color="#FFD700" /><Text style={{ color: '#FFD700', fontSize: 12, fontWeight: '700' }}>Hot</Text></>
                                                                ) : isCold ? (
                                                                    <><FaSnowflake size={12} color="#6688aa" /><Text style={{ color: '#6688aa', fontSize: 12, fontWeight: '600' }}>{t('app.admin.statusCold')}</Text></>
                                                                ) : (
                                                                    <Text style={{ color: '#44ff44', fontSize: 12, fontWeight: '600' }}>{t('app.admin.statusActive')}</Text>
                                                                )}
                                                            </View>
                                                            <Text style={[styles.tableCellText, { width: 140, fontSize: 12 }]}>
                                                                {listener.last_action_at
                                                                    ? new Date(listener.last_action_at).toLocaleString()
                                                                    : '-'}
                                                            </Text>
                                                            <View style={{ width: 90, justifyContent: 'center' }}>
                                                                <TouchableOpacity
                                                                    style={styles.actionButton}
                                                                    onPress={() => openListenerAnalytics(listener.listener_id)}
                                                                >
                                                                    <Text style={styles.actionButtonText}>{t('app.admin.details')}</Text>
                                                                </TouchableOpacity>
                                                            </View>
                                                        </View>
                                                    );
                                                })}
                                            {analyticsData.listeners.length === 0 && (
                                                <Text style={styles.emptyText}>{t('app.admin.noActiveUsers')}</Text>
                                            )}
                                        </View>
                                    </ScrollView>
                                </View>

                                {/* Inactive Users */}
                                {analyticsData.inactive_users.length > 0 && (
                                    <View style={styles.statsCard}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <FaSnowflake size={16} color="#ff6b6b" />
                                            <Text style={styles.statsCardTitle}>Inactive Users (No activity 14+ days)</Text>
                                        </View>
                                        <View style={{ gap: 6 }}>
                                            {analyticsData.inactive_users.map((user) => (
                                                <View key={user.listener_id} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: THEME.border }}>
                                                    <View>
                                                        <Text style={styles.tableCellTextBold}>{user.email}</Text>
                                                        {user.username && user.username !== user.email && (
                                                            <Text style={styles.tableCellTextIds}>{user.username}</Text>
                                                        )}
                                                    </View>
                                                    <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
                                                        Registered: {user.registered_at ? new Date(user.registered_at).toLocaleDateString() : '-'}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                    </View>
                                )}

                                <TouchableOpacity
                                    style={styles.refreshButton}
                                    onPress={() => loadAnalytics()}
                                >
                                    <Text style={styles.refreshButtonText}>{t('app.admin.refreshAnalytics')}</Text>
                                </TouchableOpacity>
                            </View>
                        ) : (
                            <Text style={styles.emptyText}>Failed to load analytics data.</Text>
                        )}
                    </View>
                )}

                {activeTab === 'listeners_data' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{t('app.admin.listenersDataOverview')}</Text>
                        <Text style={styles.sectionSubtitle}>
                            Aggregated data from listeners, lists, and messages.
                        </Text>

                        {listenersDataLoading ? (
                            <ActivityIndicator size="large" color="#4DA6FF" style={{ marginTop: 20 }} />
                        ) : (
                            <ScrollView horizontal>
                                <View style={styles.table}>
                                    <View style={styles.tableHeader}>
                                        <Text style={[styles.tableHeaderText, { width: 250 }]}>Email / Username</Text>
                                        <Text style={[styles.tableHeaderText, { width: 80 }]}>Lists</Text>
                                        <Text style={[styles.tableHeaderText, { width: 80 }]}>Follows</Text>
                                        <Text style={[styles.tableHeaderText, { width: 80 }]}>Tweets</Text>
                                        <Text style={[styles.tableHeaderText, { width: 80 }]}>Telegram</Text>
                                        <Text style={[styles.tableHeaderText, { width: 150 }]}>Last Active</Text>
                                        <Text style={[styles.tableHeaderText, { width: 150 }]}>Created At</Text>
                                        <Text style={[styles.tableHeaderText, { width: 100 }]}>Actions</Text>
                                    </View>
                                    {listenersData.map((listener) => (
                                        <View key={listener.id} style={styles.tableRow}>
                                            <View style={{ width: 250 }}>
                                                <Text style={styles.tableCellTextBold}>{listener.email}</Text>
                                                <Text style={styles.tableCellTextIds}>{listener.username}</Text>
                                                <Text style={styles.tableCellTextIds}>{listener.id}</Text>
                                            </View>
                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.lists_count}</Text>
                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.follows_count || 0}</Text>
                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.tweets_count}</Text>
                                            <Text style={[styles.tableCellText, { width: 80 }]}>{listener.telegram_count}</Text>
                                            <Text style={[styles.tableCellText, { width: 150 }]}>
                                                {listener.last_active_at ? new Date(listener.last_active_at).toLocaleString() : (listener.updated_at ? new Date(listener.updated_at).toLocaleDateString() : '-')}
                                            </Text>
                                            <Text style={[styles.tableCellText, { width: 150 }]}>
                                                {new Date(listener.created_at).toLocaleDateString()}
                                            </Text>
                                            <View style={{ width: 100, justifyContent: 'center', gap: 4 }}>
                                                <TouchableOpacity
                                                    style={styles.actionButton}
                                                    onPress={() => openListenerAnalytics(listener.id)}
                                                >
                                                    <Text style={styles.actionButtonText}>{t('app.admin.analyticsBtn')}</Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity
                                                    style={[styles.actionButton, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: THEME.border }]}
                                                    onPress={() => openActivityModal(listener)}
                                                >
                                                    <Text style={[styles.actionButtonText, { color: THEME.textMuted }]}>{t('app.admin.activityLog')}</Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity
                                                    style={[styles.actionButton, { backgroundColor: 'rgba(77,166,255,0.08)', borderColor: '#4DA6FF55' }]}
                                                    onPress={() => openManageUserModal(listener)}
                                                >
                                                    <Text style={[styles.actionButtonText, { color: '#4DA6FF' }]}>Manage</Text>
                                                </TouchableOpacity>
                                            </View>
                                        </View>
                                    ))}
                                    {listenersData.length === 0 && (
                                        <Text style={styles.emptyText}>{t('app.admin.noDataFound')}</Text>
                                    )}
                                </View>
                            </ScrollView>
                        )}
                    </View>
                )}

                {activeTab === 'stats' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{t('app.admin.systemDashboard')}</Text>
                        <Text style={styles.sectionSubtitle}>
                            Real-time overview of system health and content metrics.
                        </Text>

                        {statsLoading ? (
                            <ActivityIndicator size="large" color="#4DA6FF" style={{ marginTop: 20 }} />
                        ) : detailedStats ? (
                            <View style={{ gap: 20 }}>
                                {/* 1. System Pulse Row */}
                                <View style={styles.statsRow}>
                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <View style={styles.statsHeader}>
                                            <FaChartBar size={20} color="#4DA6FF" />
                                            <Text style={styles.statsCardTitle}>{t('app.admin.systemPulse')}</Text>
                                        </View>
                                        <View style={styles.statsGrid}>
                                            <View style={styles.statItem}>
                                                <Text style={[styles.statValue, { color: detailedStats.counts.tweets_pending > 100 ? '#ffaa00' : '#44ff44' }]}>
                                                    {detailedStats.counts.tweets_pending}
                                                </Text>
                                                <Text style={styles.statLabel}>{t('app.admin.pendingTweets')}</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{detailedStats.counts.listeners}</Text>
                                                <Text style={styles.statLabel}>{t('app.admin.totalListeners')}</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{detailedStats.counts.tweets_analyzed}</Text>
                                                <Text style={styles.statLabel}>{t('app.admin.analyzedTweets')}</Text>
                                            </View>
                                        </View>
                                    </View>

                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <View style={styles.statsHeader}>
                                            <FaChartBar size={20} color="#A64DFF" />
                                            <Text style={styles.statsCardTitle}>Last 24 Hours</Text>
                                        </View>
                                        <View style={styles.statsGrid}>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{detailedStats.activity.tweets_24h}</Text>
                                                <Text style={styles.statLabel}>{t('app.admin.newTweets')}</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{detailedStats.activity.telegram_24h}</Text>
                                                <Text style={styles.statLabel}>{t('app.admin.telegramMsgs')}</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{detailedStats.activity.notifications_24h}</Text>
                                                <Text style={styles.statLabel}>{t('app.admin.notifications')}</Text>
                                            </View>
                                        </View>
                                    </View>
                                </View>

                                {/* 2. Sources Breakdown */}
                                <View style={styles.statsRow}>
                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <Text style={styles.statsCardTitle}>{t('app.admin.monitoringScale')}</Text>
                                        <View style={{ marginTop: 10, gap: 10 }}>
                                            <View style={styles.rowBetween}>
                                                <Text style={styles.rowLabel}>{t('app.admin.twitterAccountsTracked')}</Text>
                                                <Text style={styles.rowValue}>{detailedStats.counts.users_tracked}</Text>
                                            </View>
                                            <View style={styles.rowBetween}>
                                                <Text style={styles.rowLabel}>Total Follows (Twitter)</Text>
                                                <Text style={styles.rowValue}>{detailedStats.counts.follows_twitter}</Text>
                                            </View>
                                            <View style={styles.rowBetween}>
                                                <Text style={styles.rowLabel}>Total Follows (Telegram)</Text>
                                                <Text style={styles.rowValue}>{detailedStats.counts.follows_telegram}</Text>
                                            </View>
                                            <View style={styles.rowBetween}>
                                                <Text style={styles.rowLabel}>{t('app.admin.telegramChannels')}</Text>
                                                <Text style={styles.rowValue}>{detailedStats.counts.telegram_channels}</Text>
                                            </View>
                                        </View>
                                    </View>

                                    <View style={[styles.statsCard, styles.flexCard]}>
                                        <Text style={styles.statsCardTitle}>{t('app.admin.topFollowedProfiles')}</Text>
                                        <View style={{ marginTop: 10, gap: 8 }}>
                                            {detailedStats.top_follows.map((item, index) => (
                                                <View key={index} style={styles.rowBetween}>
                                                    <Text style={styles.rowLabel}>@{item.username} ({item.source})</Text>
                                                    <Text style={styles.rowValue}>{item.listener_count} listeners</Text>
                                                </View>
                                            ))}
                                            {detailedStats.top_follows.length === 0 && <Text style={styles.emptyText}>No data</Text>}
                                        </View>
                                    </View>
                                </View>



                                {/* 3. Trends & Narratives */}
                                {detailedStats.trends && (
                                    <View style={styles.statsRow}>
                                        <View style={[styles.statsCard, styles.flexCard]}>
                                            <View style={styles.statsHeader}>
                                                <FaChartBar size={20} color="#FFD700" />
                                                <Text style={styles.statsCardTitle}>{t('app.admin.narrativeDetection')}</Text>
                                            </View>
                                            <View style={styles.statsGrid}>
                                                <View style={styles.statItem}>
                                                    <Text style={styles.statValue}>{detailedStats.trends.active_narratives}</Text>
                                                    <Text style={styles.statLabel}>{t('app.admin.activeNarratives')}</Text>
                                                </View>
                                                <View style={styles.statItem}>
                                                    <Text style={[styles.statValue, { color: detailedStats.trends.trending_narratives > 0 ? '#FFD700' : '#fff' }]}>
                                                        {detailedStats.trends.trending_narratives}
                                                    </Text>
                                                    <Text style={styles.statLabel}>{t('app.admin.avgTrending')}</Text>
                                                </View>
                                                <View style={styles.statItem}>
                                                    <Text style={styles.statValue}>{detailedStats.trends.total_narratives}</Text>
                                                    <Text style={styles.statLabel}>Total All Time</Text>
                                                </View>
                                            </View>
                                        </View>

                                        <View style={[styles.statsCard, styles.flexCard]}>
                                            <Text style={styles.statsCardTitle}>Top Trending Now</Text>
                                            <View style={{ marginTop: 10, gap: 8 }}>
                                                {detailedStats.trends.top_trending.map((item, index) => (
                                                    <View key={index} style={styles.rowBetween}>
                                                        <Text style={[styles.rowLabel, { maxWidth: 200 }]} numberOfLines={1}>
                                                            {item.title || 'Untitled Narrative'}
                                                        </Text>
                                                        <View style={{ alignItems: 'flex-end' }}>
                                                            <Text style={styles.rowValue}>{item.message_count} msgs</Text>
                                                            <Text style={{ fontSize: 10, color: '#FFD700' }}>
                                                                {item.velocity_1h.toFixed(1)}/hr
                                                            </Text>
                                                        </View>
                                                    </View>
                                                ))}
                                                {detailedStats.trends.top_trending.length === 0 && (
                                                    <Text style={styles.emptyText}>No currently trending narratives</Text>
                                                )}
                                            </View>
                                        </View>
                                    </View>
                                )}

                                {/* 4. Database Stats (Existing) */}
                                {dbStats && (
                                    <View style={styles.statsCard}>
                                        <View style={styles.statsHeader}>
                                            <FaDatabase size={20} color="#4DA6FF" />
                                            <Text style={styles.statsCardTitle}>Database Health</Text>
                                        </View>
                                        <View style={styles.statsGrid}>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{dbStats.pool_size}</Text>
                                                <Text style={styles.statLabel}>Pool Size</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={styles.statValue}>{dbStats.pool_free}</Text>
                                                <Text style={styles.statLabel}>Free Conn</Text>
                                            </View>
                                            <View style={styles.statItem}>
                                                <Text style={[styles.statValue, { color: dbStats.pool_used > 15 ? 'red' : '#4DA6FF' }]}>
                                                    {dbStats.pool_used}
                                                </Text>
                                                <Text style={styles.statLabel}>Used Conn</Text>
                                            </View>
                                        </View>
                                    </View>
                                )}

                                <TouchableOpacity
                                    style={styles.refreshButton}
                                    onPress={loadDetailedStats}
                                >
                                    <Text style={styles.refreshButtonText}>{t('app.admin.refreshDashboard')}</Text>
                                </TouchableOpacity>
                            </View>
                        ) : (
                            <Text style={styles.emptyText}>Failed to load statistics.</Text>
                        )}
                    </View>
                )
                }

                {activeTab === 'organizations' && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Organizations</Text>
                        <Text style={styles.sectionSubtitle}>
                            Create and manage organizations. Assign members and control access.
                        </Text>

                        {/* Create Organization */}
                        <View style={styles.form}>
                            <Text style={[styles.sectionTitle, { fontSize: 20, marginBottom: 16 }]}>Create Organization</Text>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Name *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="Organization name"
                                    placeholderTextColor="#666"
                                    value={newOrg.name}
                                    onChangeText={(text) => {
                                        const slug = text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                                        setNewOrg({ ...newOrg, name: text, slug });
                                    }}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Slug *</Text>
                                <Text style={styles.helperText}>URL-safe identifier, e.g. "acme-pr"</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="e.g. acme-pr"
                                    placeholderTextColor="#666"
                                    value={newOrg.slug}
                                    onChangeText={(text) => setNewOrg({ ...newOrg, slug: text })}
                                    autoCapitalize="none"
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Plan</Text>
                                <select
                                    value={newOrg.plan}
                                    onChange={e => setNewOrg({ ...newOrg, plan: e.target.value })}
                                    style={{
                                        backgroundColor: THEME.bg,
                                        color: THEME.textPrimary,
                                        border: `1px solid ${THEME.border}`,
                                        borderRadius: 8,
                                        padding: '10px 12px',
                                        fontSize: 14,
                                        width: '100%',
                                    }}
                                >
                                    <option value="free">Free</option>
                                    <option value="pro">Pro</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>First Owner (optional)</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="Search by email..."
                                    placeholderTextColor="#666"
                                    value={newOrgOwnerSearch}
                                    onChangeText={searchOrgOwner}
                                />
                                {newOrgOwnerResults.length > 0 && !newOrgOwner && (
                                    <View style={{ backgroundColor: THEME.bgCard, borderRadius: 8, borderWidth: 1, borderColor: THEME.border, marginTop: 4 }}>
                                        {newOrgOwnerResults.slice(0, 5).map(listener => (
                                            <TouchableOpacity
                                                key={listener.id}
                                                style={{ padding: 10, borderBottomWidth: 1, borderBottomColor: THEME.border }}
                                                onPress={() => {
                                                    setNewOrgOwner(listener);
                                                    setNewOrgOwnerSearch(listener.email);
                                                    setNewOrgOwnerResults([]);
                                                }}
                                            >
                                                <Text style={styles.tableCellTextBold}>{listener.email}</Text>
                                                {listener.username && <Text style={styles.tableCellTextIds}>{listener.username}</Text>}
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                )}
                                {newOrgOwner && (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 8 }}>
                                        <FaCheck size={14} color="#44ff44" />
                                        <Text style={[styles.tableCellText, { color: '#44ff44' }]}>Selected: {newOrgOwner.email}</Text>
                                        <TouchableOpacity onPress={() => { setNewOrgOwner(null); setNewOrgOwnerSearch(''); }}>
                                            <FaTimes size={14} color="#ff4444" />
                                        </TouchableOpacity>
                                    </View>
                                )}
                            </View>

                            {createOrgError ? (
                                <View style={styles.errorContainer}>
                                    <FaTimes size={16} color="#ff4444" />
                                    <Text style={styles.errorText}>{createOrgError}</Text>
                                </View>
                            ) : null}

                            {createOrgSuccess ? (
                                <View style={styles.successContainer}>
                                    <FaCheck size={16} color="#44ff44" />
                                    <Text style={styles.successText}>{createOrgSuccess}</Text>
                                </View>
                            ) : null}

                            <TouchableOpacity
                                style={[styles.submitButton, createOrgLoading && styles.submitButtonDisabled]}
                                onPress={handleCreateOrg}
                                disabled={createOrgLoading}
                            >
                                {createOrgLoading ? (
                                    <ActivityIndicator size="small" color="#fff" />
                                ) : (
                                    <>
                                        <FaBuilding size={18} color="#fff" />
                                        <Text style={styles.submitButtonText}>Create Organization</Text>
                                    </>
                                )}
                            </TouchableOpacity>
                        </View>

                        {/* Manage Organizations */}
                        <Text style={[styles.sectionTitle, { fontSize: 20, marginBottom: 12 }]}>Manage Organizations</Text>

                        <TextInput
                            style={styles.searchInput}
                            placeholder="Search by name or slug..."
                            placeholderTextColor="#666"
                            value={orgSearch}
                            onChangeText={(text) => {
                                setOrgSearch(text);
                                loadOrgs(text);
                            }}
                        />

                        {orgsLoading ? (
                            <ActivityIndicator size="large" color="#4DA6FF" style={{ marginTop: 20 }} />
                        ) : (
                            <ScrollView horizontal>
                                <View style={styles.table}>
                                    <View style={styles.tableHeader}>
                                        <Text style={[styles.tableHeaderText, { width: 160 }]}>Name</Text>
                                        <Text style={[styles.tableHeaderText, { width: 140 }]}>Slug</Text>
                                        <Text style={[styles.tableHeaderText, { width: 90 }]}>Plan</Text>
                                        <Text style={[styles.tableHeaderText, { width: 80 }]}>Members</Text>
                                        <Text style={[styles.tableHeaderText, { width: 180 }]}>Owner Email</Text>
                                        <Text style={[styles.tableHeaderText, { width: 120 }]}>Created</Text>
                                        <Text style={[styles.tableHeaderText, { width: 260 }]}>Actions</Text>
                                    </View>

                                    {orgs.map((org) => (
                                        <View key={org.id}>
                                            {/* Org row */}
                                            <View style={styles.tableRow}>
                                                {editingOrgId === org.id ? (
                                                    <View style={{ width: 160 }}>
                                                        <TextInput
                                                            style={[styles.input, { padding: 4, fontSize: 13 }]}
                                                            value={editingOrgData.name}
                                                            onChangeText={t => setEditingOrgData(d => ({ ...d, name: t }))}
                                                        />
                                                    </View>
                                                ) : (
                                                    <View style={{ width: 160 }}>
                                                        <Text style={styles.tableCellTextBold}>{org.name}</Text>
                                                    </View>
                                                )}

                                                {editingOrgId === org.id ? (
                                                    <View style={{ width: 140 }}>
                                                        <TextInput
                                                            style={[styles.input, { padding: 4, fontSize: 13 }]}
                                                            value={editingOrgData.slug}
                                                            onChangeText={t => setEditingOrgData(d => ({ ...d, slug: t }))}
                                                        />
                                                    </View>
                                                ) : (
                                                    <Text style={[styles.tableCellText, { width: 140 }]}>{org.slug}</Text>
                                                )}

                                                {editingOrgId === org.id ? (
                                                    <View style={{ width: 90 }}>
                                                        <select
                                                            value={editingOrgData.plan}
                                                            onChange={e => setEditingOrgData(d => ({ ...d, plan: e.target.value }))}
                                                            style={{ backgroundColor: THEME.bg, color: THEME.textPrimary, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: '4px 8px', fontSize: 12, width: '100%' }}
                                                        >
                                                            <option value="free">free</option>
                                                            <option value="pro">pro</option>
                                                            <option value="enterprise">enterprise</option>
                                                        </select>
                                                    </View>
                                                ) : (
                                                    <Text style={[styles.tableCellText, { width: 90 }]}>{org.plan || '-'}</Text>
                                                )}

                                                <Text style={[styles.tableCellText, { width: 80 }]}>{org.member_count ?? org.members_count ?? '-'}</Text>
                                                <Text style={[styles.tableCellText, { width: 180 }]} numberOfLines={1}>{org.owner_email || '-'}</Text>
                                                <Text style={[styles.tableCellText, { width: 120 }]}>
                                                    {org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}
                                                </Text>

                                                <View style={{ width: 260, flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                                                    {editingOrgId === org.id ? (
                                                        <>
                                                            <TouchableOpacity
                                                                style={[styles.actionButton, { backgroundColor: 'rgba(68,255,68,0.15)', borderColor: 'rgba(68,255,68,0.4)' }]}
                                                                onPress={handleSaveEditOrg}
                                                                disabled={editOrgLoading}
                                                            >
                                                                <Text style={[styles.actionButtonText, { color: '#44ff44' }]}>Save</Text>
                                                            </TouchableOpacity>
                                                            <TouchableOpacity
                                                                style={[styles.actionButton, { backgroundColor: 'rgba(255,255,255,0.08)' }]}
                                                                onPress={() => setEditingOrgId(null)}
                                                            >
                                                                <Text style={styles.actionButtonText}>Cancel</Text>
                                                            </TouchableOpacity>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TouchableOpacity
                                                                style={styles.actionButton}
                                                                onPress={() => toggleExpandOrg(org.id)}
                                                            >
                                                                <Text style={styles.actionButtonText}>
                                                                    {expandedOrgId === org.id ? 'Hide' : 'Members'}
                                                                </Text>
                                                            </TouchableOpacity>
                                                            <TouchableOpacity
                                                                style={[styles.actionButton, { backgroundColor: 'rgba(255,200,0,0.15)', borderColor: 'rgba(255,200,0,0.4)' }]}
                                                                onPress={() => handleEditOrg(org)}
                                                            >
                                                                <Text style={[styles.actionButtonText, { color: '#ffc800' }]}>Edit</Text>
                                                            </TouchableOpacity>
                                                            {deleteOrgConfirmId === org.id ? (
                                                                <>
                                                                    <TouchableOpacity
                                                                        style={[styles.actionButton, { backgroundColor: 'rgba(255,68,68,0.2)', borderColor: 'rgba(255,68,68,0.5)' }]}
                                                                        onPress={() => handleDeleteOrg(org.id)}
                                                                        disabled={deleteOrgLoading}
                                                                    >
                                                                        <Text style={[styles.actionButtonText, { color: '#ff4444' }]}>Confirm</Text>
                                                                    </TouchableOpacity>
                                                                    <TouchableOpacity
                                                                        style={styles.actionButton}
                                                                        onPress={() => setDeleteOrgConfirmId(null)}
                                                                    >
                                                                        <Text style={styles.actionButtonText}>No</Text>
                                                                    </TouchableOpacity>
                                                                </>
                                                            ) : (
                                                                <TouchableOpacity
                                                                    style={[styles.actionButton, { backgroundColor: 'rgba(255,68,68,0.1)', borderColor: 'rgba(255,68,68,0.3)' }]}
                                                                    onPress={() => setDeleteOrgConfirmId(org.id)}
                                                                >
                                                                    <Text style={[styles.actionButtonText, { color: '#ff4444' }]}>Delete</Text>
                                                                </TouchableOpacity>
                                                            )}
                                                        </>
                                                    )}
                                                </View>
                                            </View>

                                            {/* Expanded members section */}
                                            {expandedOrgId === org.id && (
                                                <View style={{ backgroundColor: 'rgba(77,166,255,0.05)', padding: 16, borderBottomWidth: 1, borderBottomColor: THEME.border }}>
                                                    {expandedOrgDetailLoading ? (
                                                        <ActivityIndicator size="small" color="#4DA6FF" />
                                                    ) : expandedOrgDetail ? (
                                                        <View>
                                                            <Text style={[styles.label, { marginBottom: 8 }]}>Members ({(expandedOrgDetail.members || []).length})</Text>

                                                            {(expandedOrgDetail.members || []).map(member => (
                                                                <View key={member.listener_id || member.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: THEME.border }}>
                                                                    <View style={{ flex: 1 }}>
                                                                        <Text style={styles.tableCellTextBold}>{member.email || member.username || member.listener_id}</Text>
                                                                        {member.username && member.email && <Text style={styles.tableCellTextIds}>{member.username}</Text>}
                                                                    </View>
                                                                    <View style={{ backgroundColor: 'rgba(77,166,255,0.2)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 }}>
                                                                        <Text style={{ color: '#4DA6FF', fontSize: 11, fontWeight: '700' }}>{member.role?.toUpperCase()}</Text>
                                                                    </View>
                                                                    <select
                                                                        value={member.role}
                                                                        onChange={e => handleChangeMemberRole(org.id, member.listener_id || member.id, e.target.value)}
                                                                        style={{ backgroundColor: THEME.bg, color: THEME.textPrimary, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
                                                                    >
                                                                        <option value="owner">Owner</option>
                                                                        <option value="admin">Admin</option>
                                                                        <option value="member">Member</option>
                                                                        <option value="viewer">Viewer</option>
                                                                    </select>
                                                                    <TouchableOpacity
                                                                        style={[styles.actionButton, { backgroundColor: 'rgba(255,68,68,0.1)', borderColor: 'rgba(255,68,68,0.3)' }]}
                                                                        onPress={() => handleRemoveMember(org.id, member.listener_id || member.id)}
                                                                    >
                                                                        <Text style={[styles.actionButtonText, { color: '#ff4444' }]}>Remove</Text>
                                                                    </TouchableOpacity>
                                                                </View>
                                                            ))}

                                                            {(expandedOrgDetail.members || []).length === 0 && (
                                                                <Text style={styles.emptyText}>No members yet</Text>
                                                            )}

                                                            {/* Add Member row */}
                                                            <View style={{ marginTop: 12, flexDirection: 'row', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                                <View style={{ flex: 1, minWidth: 200 }}>
                                                                    <TextInput
                                                                        style={styles.input}
                                                                        placeholder="Search listener by email..."
                                                                        placeholderTextColor="#666"
                                                                        value={addMemberSearch[org.id] || ''}
                                                                        onChangeText={text => searchAddMember(org.id, text)}
                                                                    />
                                                                    {(addMemberResults[org.id] || []).length > 0 && (
                                                                        <View style={{ backgroundColor: THEME.bgCard, borderRadius: 8, borderWidth: 1, borderColor: THEME.border, marginTop: 4 }}>
                                                                            {(addMemberResults[org.id] || []).slice(0, 5).map(listener => (
                                                                                <TouchableOpacity
                                                                                    key={listener.id}
                                                                                    style={{ padding: 8, borderBottomWidth: 1, borderBottomColor: THEME.border }}
                                                                                    onPress={() => handleAddMember(org.id, listener)}
                                                                                >
                                                                                    <Text style={styles.tableCellTextBold}>{listener.email}</Text>
                                                                                    {listener.username && <Text style={styles.tableCellTextIds}>{listener.username}</Text>}
                                                                                </TouchableOpacity>
                                                                            ))}
                                                                        </View>
                                                                    )}
                                                                </View>
                                                                <select
                                                                    value={addMemberRole[org.id] || 'member'}
                                                                    onChange={e => setAddMemberRole(prev => ({ ...prev, [org.id]: e.target.value }))}
                                                                    style={{ backgroundColor: THEME.bg, color: THEME.textPrimary, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                                                                >
                                                                    <option value="owner">Owner</option>
                                                                    <option value="admin">Admin</option>
                                                                    <option value="member">Member</option>
                                                                    <option value="viewer">Viewer</option>
                                                                </select>
                                                            </View>
                                                            {/* Tags section */}
                                                            <AdminOrgTags orgId={org.id} />
                                                        </View>
                                                    ) : (
                                                        <Text style={styles.emptyText}>Failed to load members</Text>
                                                    )}
                                                </View>
                                            )}
                                        </View>
                                    ))}

                                    {orgs.length === 0 && !orgsLoading && (
                                        <Text style={styles.emptyText}>No organizations found</Text>
                                    )}
                                </View>
                            </ScrollView>
                        )}
                    </View>
                )}

                {activeTab === 'article_analyzer' && (
                    <ArticleAnalyzerTab />
                )}
                {activeTab === 'product_detection' && (
                    <ProductDetectionTab />
                )}
                {activeTab === 'demo_generator' && (
                    <DemoGeneratorTab />
                )}
                    </ScrollView>
                </div>
            </div>

            <Modal
                visible={isActivityModalVisible}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setIsActivityModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Activity: {selectedListenerName}</Text>
                            <TouchableOpacity onPress={() => setIsActivityModalVisible(false)} style={styles.closeButton}>
                                <FaTimes size={20} color="#888" />
                            </TouchableOpacity>
                        </View>

                        {activityLoading ? (
                            <ActivityIndicator size="large" color="#4DA6FF" style={{ margin: 20 }} />
                        ) : (
                            <ScrollView style={{ maxHeight: '80%' }}>
                                {selectedListenerActivity && selectedListenerActivity.length > 0 ? (
                                    selectedListenerActivity.map((activity) => (
                                        <View key={activity.id} style={styles.activityItem}>
                                            <Text style={styles.activityAction}>{activity.action_type.replace(/_/g, ' ').toUpperCase()}</Text>
                                            <Text style={styles.activityDate}>{new Date(activity.created_at).toLocaleString()}</Text>
                                            {activity.details && Object.keys(activity.details).length > 0 && (
                                                <Text style={styles.activityDetails}>
                                                    {JSON.stringify(activity.details, null, 2)}
                                                </Text>
                                            )}
                                        </View>
                                    ))
                                ) : (
                                    <Text style={styles.emptyText}>No activity recorded</Text>
                                )}
                            </ScrollView>
                        )}
                    </View>
                </View>
            </Modal>

            {/* Listener Analytics Detail Modal */}
            <Modal
                visible={isListenerAnalyticsModalVisible}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setIsListenerAnalyticsModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={[styles.modalContent, { maxWidth: 900, maxHeight: '90%' }]}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>
                                {listenerAnalytics ? `${listenerAnalytics.profile.email}` : 'Loading...'}
                            </Text>
                            <TouchableOpacity onPress={() => setIsListenerAnalyticsModalVisible(false)} style={styles.closeButton}>
                                <FaTimes size={20} color="#888" />
                            </TouchableOpacity>
                        </View>

                        {listenerAnalyticsLoading ? (
                            <ActivityIndicator size="large" color="#4DA6FF" style={{ margin: 40 }} />
                        ) : listenerAnalytics ? (
                            <ScrollView style={{ maxHeight: '85%' }}>
                                {/* Profile Header */}
                                <View style={{ flexDirection: 'row', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                                    <View style={[styles.statItem, { flex: 1, minWidth: 120 }]}>
                                        <Text style={{ color: THEME.textMuted, fontSize: 11, marginBottom: 4 }}>Username</Text>
                                        <Text style={{ color: THEME.textPrimary, fontSize: 14, fontWeight: '600' }}>
                                            {listenerAnalytics.profile.username || '-'}
                                        </Text>
                                    </View>
                                    <View style={[styles.statItem, { flex: 1, minWidth: 120 }]}>
                                        <Text style={{ color: THEME.textMuted, fontSize: 11, marginBottom: 4 }}>Registered</Text>
                                        <Text style={{ color: THEME.textPrimary, fontSize: 14, fontWeight: '600' }}>
                                            {listenerAnalytics.profile.created_at
                                                ? new Date(listenerAnalytics.profile.created_at).toLocaleDateString()
                                                : '-'}
                                        </Text>
                                    </View>
                                    <View style={[styles.statItem, { flex: 1, minWidth: 120 }]}>
                                        <Text style={{ color: THEME.textMuted, fontSize: 11, marginBottom: 4 }}>Language</Text>
                                        <Text style={{ color: THEME.textPrimary, fontSize: 14, fontWeight: '600' }}>
                                            {listenerAnalytics.profile.preferred_language || 'en'}
                                        </Text>
                                    </View>
                                </View>

                                {/* Engagement Summary */}
                                <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                    <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>Engagement Summary</Text>
                                    <View style={styles.statsGrid}>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24 }]}>{listenerAnalytics.engagement.actions_24h}</Text>
                                            <Text style={styles.statLabel}>Actions (24h)</Text>
                                        </View>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24, color: '#FFD700' }]}>{listenerAnalytics.engagement.actions_7d}</Text>
                                            <Text style={styles.statLabel}>Actions (7d)</Text>
                                        </View>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24 }]}>{listenerAnalytics.engagement.total_actions}</Text>
                                            <Text style={styles.statLabel}>Total ({listenerAnalytics.engagement.period_days}d)</Text>
                                        </View>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24, color: '#44ff44' }]}>{listenerAnalytics.engagement.active_days}</Text>
                                            <Text style={styles.statLabel}>Active Days</Text>
                                        </View>
                                    </View>
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 12 }}>
                                        <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
                                            First active: {listenerAnalytics.engagement.first_activity
                                                ? new Date(listenerAnalytics.engagement.first_activity).toLocaleDateString()
                                                : 'Never'}
                                        </Text>
                                        <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
                                            Last active: {listenerAnalytics.engagement.last_activity
                                                ? new Date(listenerAnalytics.engagement.last_activity).toLocaleString()
                                                : 'Never'}
                                        </Text>
                                    </View>
                                </View>

                                {/* Content Counts */}
                                <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                    <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>Content & Monitoring</Text>
                                    <View style={styles.statsGrid}>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24 }]}>{listenerAnalytics.counts.lists}</Text>
                                            <Text style={styles.statLabel}>Lists</Text>
                                        </View>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24 }]}>{listenerAnalytics.counts.follows}</Text>
                                            <Text style={styles.statLabel}>Total Follows</Text>
                                        </View>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24 }]}>{listenerAnalytics.counts.tweets}</Text>
                                            <Text style={styles.statLabel}>Tweets</Text>
                                        </View>
                                        <View style={styles.statItem}>
                                            <Text style={[styles.statValue, { fontSize: 24 }]}>{listenerAnalytics.counts.telegram}</Text>
                                            <Text style={styles.statLabel}>Telegram</Text>
                                        </View>
                                    </View>
                                    {Object.keys(listenerAnalytics.counts.follows_by_source || {}).length > 0 && (
                                        <View style={{ marginTop: 12, gap: 4 }}>
                                            <Text style={{ color: THEME.textMuted, fontSize: 12, marginBottom: 4 }}>Follows by source:</Text>
                                            <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
                                                {Object.entries(listenerAnalytics.counts.follows_by_source).map(([source, cnt]) => (
                                                    <View key={source} style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                                                        <View style={{
                                                            width: 8, height: 8, borderRadius: 4,
                                                            backgroundColor: source === 'twitter' ? '#1da1f2' : source === 'telegram' ? '#0088cc' : source === 'tiktok' ? '#ff0050' : source === 'instagram' ? '#e1306c' : '#888'
                                                        }} />
                                                        <Text style={{ color: THEME.textPrimary, fontSize: 13 }}>{source}: {cnt}</Text>
                                                    </View>
                                                ))}
                                            </View>
                                        </View>
                                    )}
                                </View>

                                {/* Daily Activity Trend */}
                                {listenerAnalytics.daily_trend.length > 0 && (
                                    <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>Daily Activity</Text>
                                        <View style={{ height: 80, flexDirection: 'row', alignItems: 'flex-end', gap: 1 }}>
                                            {(() => {
                                                const maxA = Math.max(...listenerAnalytics.daily_trend.map(d => d.actions), 1);
                                                const bw = Math.max(3, Math.min(12, Math.floor(800 / listenerAnalytics.daily_trend.length) - 1));
                                                return listenerAnalytics.daily_trend.map((day, i) => (
                                                    <div
                                                        key={day.day}
                                                        title={`${day.day}: ${day.actions} actions`}
                                                        style={{
                                                            width: bw,
                                                            height: Math.max(2, (day.actions / maxA) * 70),
                                                            borderRadius: 2,
                                                            background: i === listenerAnalytics.daily_trend.length - 1 ? '#4DA6FF' : `rgba(77,166,255,${0.25 + (day.actions / maxA) * 0.55})`,
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                ));
                                            })()}
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>{listenerAnalytics.daily_trend[0]?.day}</Text>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>{listenerAnalytics.daily_trend[listenerAnalytics.daily_trend.length - 1]?.day}</Text>
                                        </View>
                                    </View>
                                )}

                                {/* Hourly Pattern */}
                                {listenerAnalytics.hourly_pattern.length > 0 && (
                                    <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>Active Hours (UTC)</Text>
                                        <View style={{ height: 60, flexDirection: 'row', alignItems: 'flex-end', gap: 2 }}>
                                            {(() => {
                                                const hourMap = {};
                                                listenerAnalytics.hourly_pattern.forEach(h => { hourMap[h.hour] = h.actions; });
                                                const maxH = Math.max(...Object.values(hourMap), 1);
                                                return Array.from({ length: 24 }, (_, i) => {
                                                    const val = hourMap[i] || 0;
                                                    return (
                                                        <div
                                                            key={i}
                                                            title={`${i}:00 — ${val} actions`}
                                                            style={{
                                                                flex: 1,
                                                                height: Math.max(2, (val / maxH) * 50),
                                                                borderRadius: 2,
                                                                background: val > 0 ? `rgba(77,166,255,${0.3 + (val / maxH) * 0.6})` : THEME.bg,
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    );
                                                });
                                            })()}
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>0:00</Text>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>6:00</Text>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>12:00</Text>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>18:00</Text>
                                            <Text style={{ color: THEME.textMuted, fontSize: 10 }}>23:00</Text>
                                        </View>
                                    </View>
                                )}

                                {/* Feature Usage */}
                                {listenerAnalytics.feature_usage.length > 0 && (
                                    <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>Feature Usage</Text>
                                        <View style={{ gap: 8 }}>
                                            {listenerAnalytics.feature_usage.map((feature) => {
                                                const maxC = listenerAnalytics.feature_usage[0]?.count || 1;
                                                const pct = (feature.count / maxC) * 100;
                                                const label = feature.action_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                                return (
                                                    <View key={feature.action_type} style={{ gap: 3 }}>
                                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Text style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '600' }}>{label}</Text>
                                                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                                                <Text style={{ color: THEME.textMuted, fontSize: 11 }}>
                                                                    last: {feature.last_used ? new Date(feature.last_used).toLocaleDateString() : '-'}
                                                                </Text>
                                                                <Text style={{ color: '#4DA6FF', fontSize: 12, fontWeight: '700' }}>{feature.count}</Text>
                                                            </View>
                                                        </View>
                                                        <View style={{ height: 5, borderRadius: 3, backgroundColor: THEME.bg, overflow: 'hidden' }}>
                                                            <View style={{ width: `${pct}%`, height: '100%', borderRadius: 3, backgroundColor: '#4DA6FF' }} />
                                                        </View>
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    </View>
                                )}

                                {/* Lists */}
                                {listenerAnalytics.lists.length > 0 && (
                                    <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                        <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>Lists ({listenerAnalytics.lists.length})</Text>
                                        <View style={{ gap: 6 }}>
                                            {listenerAnalytics.lists.map((list) => (
                                                <View key={list.id} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: THEME.border }}>
                                                    <View>
                                                        <Text style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '600' }}>{list.name}</Text>
                                                        {list.request && <Text style={{ color: THEME.textMuted, fontSize: 11 }} numberOfLines={1}>{list.request}</Text>}
                                                    </View>
                                                    <Text style={{ color: THEME.textMuted, fontSize: 11 }}>
                                                        {list.created_at ? new Date(list.created_at).toLocaleDateString() : '-'}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                    </View>
                                )}

                                {/* Recent Activity Log */}
                                <View style={[styles.statsCard, { marginBottom: 16 }]}>
                                    <Text style={[styles.statsCardTitle, { marginBottom: 12 }]}>
                                        Recent Activity ({listenerAnalytics.recent_activity.length})
                                    </Text>
                                    <View style={{ gap: 6 }}>
                                        {listenerAnalytics.recent_activity.slice(0, 30).map((activity) => (
                                            <View key={activity.id} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: THEME.border }}>
                                                <Text style={{ color: THEME.textMuted, fontSize: 11, width: 130, flexShrink: 0 }}>
                                                    {activity.created_at ? new Date(activity.created_at).toLocaleString() : '-'}
                                                </Text>
                                                <Text style={{ color: '#4DA6FF', fontSize: 12, fontWeight: '700', width: 160, flexShrink: 0 }}>
                                                    {activity.action_type.replace(/_/g, ' ').toUpperCase()}
                                                </Text>
                                                <Text style={{ color: THEME.textMuted, fontSize: 11, flex: 1 }} numberOfLines={1}>
                                                    {activity.details && Object.keys(activity.details).length > 0
                                                        ? Object.entries(activity.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                                                        : ''}
                                                </Text>
                                            </View>
                                        ))}
                                        {listenerAnalytics.recent_activity.length === 0 && (
                                            <Text style={styles.emptyText}>No activity recorded</Text>
                                        )}
                                    </View>
                                </View>
                            </ScrollView>
                        ) : (
                            <Text style={styles.emptyText}>Failed to load listener analytics.</Text>
                        )}
                    </View>
                </View>
            </Modal>

            {/* Manage User Modal */}
            <Modal
                visible={manageUserModalVisible}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setManageUserModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={[styles.modalContent, { maxWidth: 480 }]}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Manage User</Text>
                            <TouchableOpacity onPress={() => setManageUserModalVisible(false)} style={styles.closeButton}>
                                <FaTimes size={20} color="#888" />
                            </TouchableOpacity>
                        </View>

                        {managedListener && (
                            <View style={{ padding: 20, gap: 16 }}>
                                <Text style={{ color: THEME.textSecondary, fontSize: 14 }}>
                                    {managedListener.email}
                                </Text>

                                {/* Display Name */}
                                <View style={{ gap: 8 }}>
                                    <Text style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '600' }}>Display Name</Text>
                                    <View style={{ flexDirection: 'row', gap: 8 }}>
                                        <TextInput
                                            style={[styles.input, { flex: 1 }]}
                                            placeholder="Enter display name"
                                            placeholderTextColor={THEME.textMuted}
                                            value={managedDisplayName}
                                            onChangeText={setManagedDisplayName}
                                        />
                                        <TouchableOpacity
                                            style={[styles.actionButton, { paddingHorizontal: 16, paddingVertical: 10, justifyContent: 'center' }]}
                                            onPress={handleUpdateDisplayName}
                                            disabled={manageUserLoading || !managedDisplayName.trim()}
                                        >
                                            <Text style={styles.actionButtonText}>Save</Text>
                                        </TouchableOpacity>
                                    </View>
                                </View>

                                {/* Password Reset */}
                                <View style={{ gap: 8 }}>
                                    <Text style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '600' }}>Password Reset</Text>
                                    <TouchableOpacity
                                        style={[styles.actionButton, { paddingVertical: 10, paddingHorizontal: 16, alignSelf: 'flex-start', backgroundColor: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.3)' }]}
                                        onPress={handleSendPasswordReset}
                                        disabled={manageUserLoading}
                                    >
                                        <Text style={[styles.actionButtonText, { color: '#f59e0b' }]}>Send Password Reset Email</Text>
                                    </TouchableOpacity>
                                </View>

                                {manageUserLoading && <ActivityIndicator size="small" color="#4DA6FF" />}
                                {manageUserMsg ? <Text style={{ color: '#30d158', fontSize: 13 }}>{manageUserMsg}</Text> : null}
                                {manageUserError ? <Text style={{ color: '#ff453a', fontSize: 13 }}>{manageUserError}</Text> : null}
                            </View>
                        )}
                    </View>
                </View>
            </Modal>
        </>
    );
};


const DEMO_SEVERITY_COLORS = {
    critical: '#ff453a',
    high:     '#ff9f0a',
    medium:   '#ffd60a',
    low:      '#8e8e93',
};

const DEMO_ACTION_ICONS = {
    counter_narrative: FaFire,
    react: FaFire,
    amplify: FaFire,
    outreach: FaUsers,
    monitor: FaFire,
    respond_to_comments: FaFire,
    escalate: FaFire,
};

const DEMO_ACTION_LABELS = {
    counter_narrative: 'Counter Narrative',
    react: 'React',
    amplify: 'Amplify',
    outreach: 'Outreach',
    monitor: 'Monitor',
    respond_to_comments: 'Respond',
    escalate: 'Escalate',
};

const DEMO_PLATFORM_COLORS = {
    twitter: '#1da1f2',
    telegram: '#0088cc',
    tiktok: '#ff0050',
    instagram: '#e1306c',
};

const DEMO_EMOTION_COLORS = {
    outrage: '#ff453a', anger: '#ff453a', fear: '#bf5af2',
    mockery: '#ff9f0a', hope: '#30d158', joy: '#30d158',
    celebration: '#64d2ff', concern: '#ffd60a', skepticism: '#ac8e68', neutral: '#8e8e93',
};

function ArticleAnalyzerTab() {
    const { theme } = useTheme();
    const T = theme;
    const [orgs, setOrgs] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [lists, setLists] = useState([]);
    const [selectedListId, setSelectedListId] = useState('');
    const [listsLoading, setListsLoading] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const [queue, setQueue] = useState([]);

    useEffect(() => {
        OrgManager.adminListOrgs().then(data => setOrgs(data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!selectedOrgId) { setLists([]); setSelectedListId(''); return; }
        setListsLoading(true);
        setSelectedListId('');
        AdminManager.getOrgLists(selectedOrgId)
            .then(data => setLists(data || []))
            .catch(() => setLists([]))
            .finally(() => setListsLoading(false));
    }, [selectedOrgId]);

    const analyzeItem = async (idx, url, listId) => {
        setQueue(prev => prev.map((item, i) => i === idx ? { ...item, status: 'processing' } : item));
        try {
            const result = await AdminManager.analyzeArticle(url, listId);
            setQueue(prev => prev.map((item, i) => i === idx ? { ...item, status: 'done', result } : item));
        } catch (e) {
            setQueue(prev => prev.map((item, i) => i === idx ? { ...item, status: 'done', result: { status: 'failed', reason: e.response?.data?.detail || e.message } } : item));
        }
    };

    const addToQueue = () => {
        const url = urlInput.trim();
        if (!url || !selectedListId) return;
        if (queue.some(item => item.url === url)) return;
        const newIdx = queue.length;
        setQueue(prev => [...prev, { url, listId: selectedListId, listName: lists.find(l => l.id === selectedListId)?.name || '', status: 'pending', result: null }]);
        setUrlInput('');
        setTimeout(() => analyzeItem(newIdx, url, selectedListId), 0);
    };

    const statusColors = { relevant: '#22c55e', not_relevant: '#f59e0b', duplicate: '#6b7280', failed: '#ef4444' };
    const statusLabels = { relevant: 'רלוונטי', not_relevant: 'לא רלוונטי', duplicate: 'כפילות', failed: 'נכשל' };

    return (
        <View style={{ padding: 20 }}>
            <Text style={{ fontSize: 24, fontWeight: 'bold', color: T.textPrimary, marginBottom: 8 }}>Article Analyzer</Text>
            <Text style={{ fontSize: 14, color: T.textMuted, marginBottom: 24 }}>Paste an article URL, select org + list, and analyze for relevance.</Text>

            <View style={{ backgroundColor: T.bgCard, borderRadius: 16, padding: 20, marginBottom: 24 }}>
                <View style={{ marginBottom: 16 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: T.textPrimary, marginBottom: 8 }}>Organization</Text>
                    <select
                        value={selectedOrgId}
                        onChange={e => setSelectedOrgId(e.target.value)}
                        style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${T.border}`, backgroundColor: T.bg, color: T.textPrimary, fontSize: 14 }}
                    >
                        <option value="">Select organization...</option>
                        {orgs.filter(o => !o.is_personal).map(o => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                    </select>
                </View>

                <View style={{ marginBottom: 16 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: T.textPrimary, marginBottom: 8 }}>List</Text>
                    {listsLoading ? (
                        <ActivityIndicator size="small" color="#4DA6FF" />
                    ) : (
                        <select
                            value={selectedListId}
                            onChange={e => setSelectedListId(e.target.value)}
                            disabled={!selectedOrgId || lists.length === 0}
                            style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${T.border}`, backgroundColor: T.bg, color: T.textPrimary, fontSize: 14 }}
                        >
                            <option value="">{!selectedOrgId ? 'Select org first...' : lists.length === 0 ? 'No lists found' : 'Select list...'}</option>
                            {lists.map(l => (
                                <option key={l.id} value={l.id}>{l.name} {l.list_role ? `(${l.list_role})` : ''}</option>
                            ))}
                        </select>
                    )}
                </View>

                <View style={{ marginBottom: 16 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: T.textPrimary, marginBottom: 8 }}>Article URL</Text>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TextInput
                            value={urlInput}
                            onChangeText={setUrlInput}
                            placeholder="https://..."
                            placeholderTextColor={T.textMuted}
                            style={{ flex: 1, backgroundColor: T.bg, borderRadius: 8, padding: 12, color: T.textPrimary, fontSize: 14, borderWidth: 1, borderColor: T.border }}
                            onSubmitEditing={addToQueue}
                        />
                        <TouchableOpacity
                            onPress={addToQueue}
                            disabled={!urlInput.trim() || !selectedListId}
                            style={{ backgroundColor: !urlInput.trim() || !selectedListId ? '#555' : '#4DA6FF', borderRadius: 8, paddingHorizontal: 16, justifyContent: 'center' }}
                        >
                            <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>+</Text>
                        </TouchableOpacity>
                    </View>
                </View>

            </View>

            {queue.length > 0 && (
                <View style={{ backgroundColor: T.bgCard, borderRadius: 16, padding: 20 }}>
                    <Text style={{ fontSize: 16, fontWeight: '600', color: T.textPrimary, marginBottom: 12 }}>Queue ({queue.length})</Text>
                    {queue.map((item, idx) => (
                        <View key={idx} style={{ padding: 12, borderBottomWidth: idx < queue.length - 1 ? 1 : 0, borderBottomColor: T.border }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                {item.status === 'processing' && <ActivityIndicator size="small" color="#4DA6FF" />}
                                {item.status === 'done' && item.result && (
                                    <View style={{ backgroundColor: statusColors[item.result.status] || '#666', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 2 }}>
                                        <Text style={{ color: '#fff', fontSize: 11, fontWeight: '600' }}>{statusLabels[item.result.status] || item.result.status}</Text>
                                    </View>
                                )}
                                {item.status === 'pending' && (
                                    <View style={{ backgroundColor: '#374151', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 2 }}>
                                        <Text style={{ color: '#9ca3af', fontSize: 11 }}>ממתין</Text>
                                    </View>
                                )}
                                <Text style={{ color: T.textMuted, fontSize: 12 }}>{item.listName}</Text>
                            </View>
                            <Text style={{ color: T.textPrimary, fontSize: 13 }} numberOfLines={1}>{item.url}</Text>
                            {item.result?.title && (
                                <Text style={{ color: T.textPrimary, fontSize: 14, fontWeight: '600', marginTop: 4 }}>{item.result.title}</Text>
                            )}
                            {item.result?.summary && (
                                <Text style={{ color: T.textMuted, fontSize: 13, marginTop: 2 }}>{item.result.summary}</Text>
                            )}
                            {item.result?.reason && (
                                <Text style={{ color: T.textMuted, fontSize: 12, marginTop: 2, fontStyle: 'italic' }}>{item.result.reason}</Text>
                            )}
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
}

function DemoGeneratorTab() {
    const { theme } = useTheme();
    const T = theme;
    const [orgName, setOrgName] = useState('');
    const [orgDescription, setOrgDescription] = useState('');
    const [language, setLanguage] = useState('he');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const isRTL = language === 'he';
    const dir = isRTL ? 'rtl' : 'ltr';
    const textAlign = isRTL ? 'right' : 'left';

    const handleGenerate = async () => {
        if (!orgName.trim()) return;
        setLoading(true);
        setError('');
        setResult(null);
        try {
            const data = await AdminManager.generateDemo(orgName.trim(), orgDescription.trim() || null, language);
            setResult(data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Generation failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <View style={{ padding: 20 }}>
            <Text style={{ fontSize: 24, fontWeight: 'bold', color: T.textPrimary, marginBottom: 8 }}>
                Demo Generator
            </Text>
            <Text style={{ fontSize: 14, color: T.textMuted, marginBottom: 24 }}>
                Generate presentation-ready demo data for client pitches.
            </Text>

            {/* Input Form */}
            <View style={{ backgroundColor: T.bgCard, borderRadius: 16, padding: 20, marginBottom: 24 }}>
                <View style={{ marginBottom: 16 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: T.textPrimary, marginBottom: 8 }}>Organization Name *</Text>
                    <TextInput
                        style={{ backgroundColor: T.bg, borderRadius: 8, padding: 12, color: T.textPrimary, fontSize: 14, borderWidth: 1, borderColor: T.border }}
                        placeholder='e.g. "ELAL Airlines"'
                        placeholderTextColor="#666"
                        value={orgName}
                        onChangeText={setOrgName}
                    />
                </View>
                <View style={{ marginBottom: 16 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: T.textPrimary, marginBottom: 8 }}>Description (optional)</Text>
                    <TextInput
                        style={{ backgroundColor: T.bg, borderRadius: 8, padding: 12, color: T.textPrimary, fontSize: 14, borderWidth: 1, borderColor: T.border }}
                        placeholder='e.g. "Israeli national airline"'
                        placeholderTextColor="#666"
                        value={orgDescription}
                        onChangeText={setOrgDescription}
                    />
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: T.textPrimary }}>Language:</Text>
                    {['he', 'en'].map(l => (
                        <TouchableOpacity key={l} onPress={() => setLanguage(l)}
                            style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8, backgroundColor: language === l ? '#0a84ff' : T.bg, borderWidth: 1, borderColor: language === l ? '#0a84ff' : T.border }}>
                            <Text style={{ color: language === l ? '#fff' : T.textSecondary, fontWeight: '600', fontSize: 13 }}>{l === 'he' ? 'Hebrew' : 'English'}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
                <TouchableOpacity onPress={handleGenerate} disabled={loading || !orgName.trim()}
                    style={{ backgroundColor: '#0a84ff', borderRadius: 10, padding: 14, alignItems: 'center', opacity: loading || !orgName.trim() ? 0.5 : 1 }}>
                    {loading ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                            <ActivityIndicator color="#fff" size="small" />
                            <Text style={{ color: '#fff', fontSize: 15, fontWeight: '600' }}>Generating demo data...</Text>
                        </View>
                    ) : (
                        <Text style={{ color: '#fff', fontSize: 15, fontWeight: '600' }}>Generate Demo</Text>
                    )}
                </TouchableOpacity>
            </View>

            {error && (
                <div style={{ background: 'rgba(255,69,58,0.08)', border: '1px solid rgba(255,69,58,0.25)', padding: '12px 16px', borderRadius: 10, marginBottom: 16 }}>
                    <span style={{ color: '#ff453a', fontSize: 13 }}>{error}</span>
                </div>
            )}

            {/* ═══════════════ RESULTS ═══════════════ */}
            {result && (
                <div dir={dir} style={{ display: 'flex', flexDirection: 'column', gap: 36, direction: dir }}>

                    {/* ═══ Section 1: Recommended Actions — two-column like screenshot 1 ═══ */}
                    {result.action_items?.length > 0 && (
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted, marginBottom: 14, textAlign }}>{isRTL ? 'פעולות מומלצות' : 'RECOMMENDED ACTIONS'}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {result.action_items.map((action, i) => {
                                    const sevColor = DEMO_SEVERITY_COLORS[action.severity] || '#8e8e93';
                                    const actionLabel = DEMO_ACTION_LABELS[action.action_type] || action.action_type?.replace(/_/g, ' ');
                                    const relatedPost = result.posts?.[i];
                                    return (
                                        <div key={i} style={{ borderRadius: 14, background: T.bgCard, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                                            {/* Action title header */}
                                            <div style={{ padding: '18px 24px 14px', borderBottom: `1px solid ${T.border}` }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: `${sevColor}15`, border: `1.5px solid ${sevColor}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <FaFire size={14} color={sevColor} />
                                                    </div>
                                                    <span style={{ color: T.textPrimary, fontSize: 16, fontWeight: 700, flex: 1, textAlign }}>{action.title}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingTop: 4 }}>
                                                    <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', background: `${sevColor}18`, color: sevColor }}>{action.severity}</span>
                                                    <span style={{ color: T.textMuted, fontSize: 11 }}>·</span>
                                                    <span style={{ color: T.textMuted, fontSize: 12, fontWeight: 500 }}>{actionLabel}</span>
                                                    <span style={{ color: T.textMuted, fontSize: 11 }}>·</span>
                                                    <span style={{ color: T.textMuted, fontSize: 12 }}>{action.category}</span>
                                                </div>
                                            </div>

                                            {/* Two-column body: left = why + steps, right = source content */}
                                            <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', minHeight: 200 }}>
                                                {/* LEFT column (or RIGHT in RTL): action details */}
                                                <div style={{ flex: 1, padding: '18px 24px', borderRight: isRTL ? undefined : `1px solid ${T.border}`, borderLeft: isRTL ? `1px solid ${T.border}` : undefined }}>
                                                    {/* WHY THIS MATTERS */}
                                                    <div style={{ marginBottom: 20 }}>
                                                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted, marginBottom: 8, textAlign }}>{isRTL ? 'למה זה חשוב' : 'WHY THIS MATTERS'}</div>
                                                        <p style={{ margin: 0, color: T.textSecondary, fontSize: 14, lineHeight: '23px', textAlign }}>{action.reasoning}</p>
                                                    </div>

                                                    {/* RECOMMENDED STEPS */}
                                                    {action.suggested_steps?.length > 0 && (
                                                        <div>
                                                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#0a84ff', marginBottom: 10, textAlign }}>{isRTL ? 'צעדים מומלצים' : 'RECOMMENDED STEPS'}</div>
                                                            <div style={{ borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                                                                {action.suggested_steps.map((step, si) => (
                                                                    <div key={si} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 14px', borderBottom: si < action.suggested_steps.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                                                                        <div style={{ width: 22, height: 22, borderRadius: 11, flexShrink: 0, border: '1.5px solid rgba(10,132,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                                                                            <span style={{ color: '#0a84ff', fontSize: 10, fontWeight: 800 }}>{si + 1}</span>
                                                                        </div>
                                                                        <p style={{ margin: 0, color: T.textPrimary, fontSize: 13, lineHeight: '21px', flex: 1, textAlign }}>{step}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* RIGHT column (or LEFT in RTL): source content post */}
                                                <div style={{ width: 320, flexShrink: 0, padding: '18px 20px' }}>
                                                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted, marginBottom: 12, textAlign }}>{isRTL ? 'תוכן מקור' : 'SOURCE CONTENT'}</div>
                                                    {relatedPost && (
                                                        <div style={{ borderRadius: 10, background: T.bg, border: `1px solid ${T.border}`, padding: '14px 16px' }}>
                                                            {/* Author */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                                                <div style={{ width: 32, height: 32, borderRadius: 16, background: `${DEMO_PLATFORM_COLORS[relatedPost.source] || '#8e8e93'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                    <span style={{ fontSize: 12, fontWeight: 700, color: DEMO_PLATFORM_COLORS[relatedPost.source] || '#8e8e93' }}>{relatedPost.author_name?.[0] || '?'}</span>
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <span style={{ color: T.textPrimary, fontSize: 12, fontWeight: 600 }}>{relatedPost.author_name}</span>
                                                                    <br />
                                                                    <span style={{ color: T.textMuted, fontSize: 11 }}>@{relatedPost.username} · {relatedPost.author_followers?.toLocaleString()} {isRTL ? 'עוקבים' : 'followers'}</span>
                                                                </div>
                                                            </div>
                                                            {/* Post text */}
                                                            <p style={{ margin: '0 0 12px', color: T.textPrimary, fontSize: 13, lineHeight: '20px', textAlign }}>{relatedPost.tweet_content}</p>
                                                            {/* Metrics */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                                                <span style={{ fontSize: 10, color: T.textMuted }}>♥ {relatedPost.like_count?.toLocaleString()}</span>
                                                                <span style={{ fontSize: 10, color: T.textMuted }}>↻ {relatedPost.retweet_count?.toLocaleString()}</span>
                                                                <span style={{ fontSize: 10, color: T.textMuted }}>💬 {relatedPost.comment_count?.toLocaleString()}</span>
                                                            </div>
                                                            {/* Why included */}
                                                            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                                                                <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, marginBottom: 4, textAlign }}>{isRTL ? 'למה זה רלוונטי' : 'Why relevant'}</div>
                                                                <p style={{ margin: 0, color: T.textSecondary, fontSize: 12, lineHeight: '18px', textAlign }}>{relatedPost.reason}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ═══ Section 2: AI-Detected Posts — like screenshot 2 ═══ */}
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted, marginBottom: 14, textAlign }}>{isRTL ? 'פוסטים שזוהו ע״י AI' : 'AI-DETECTED POSTS'}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {result.posts?.map((post, i) => {
                                const platColor = DEMO_PLATFORM_COLORS[post.source] || '#8e8e93';
                                const emotionColor = DEMO_EMOTION_COLORS[post.dominant_emotion] || '#8e8e93';
                                return (
                                    <div key={i} style={{ borderRadius: 14, background: T.bgCard, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                                        {/* Title row — bold summary like screenshot 2 */}
                                        <div style={{ padding: '16px 20px 0' }}>
                                            <h3 style={{ margin: '0 0 8px', color: T.textPrimary, fontSize: 17, fontWeight: 700, lineHeight: '24px', textAlign }}>{post.summary}</h3>
                                        </div>
                                        {/* Meta row — network, date, sentiment */}
                                        <div style={{ padding: '0 20px 14px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 12, color: platColor, fontWeight: 600 }}>{isRTL ? 'רשת' : 'Network'} <span style={{ textTransform: 'capitalize' }}>{post.source}</span></span>
                                            <span style={{ color: T.textMuted, fontSize: 12 }}>{isRTL ? 'סנטימנט' : 'Sentiment'} {(post.sentiment_score * 100).toFixed(0)}%</span>
                                            <span style={{ color: emotionColor, fontSize: 12, fontWeight: 500 }}>{post.dominant_emotion}</span>
                                        </div>

                                        {/* Embedded post card — dark bg like screenshot 2 tweet embed */}
                                        <div style={{ margin: '0 20px 16px', borderRadius: 12, background: T.bg, border: `1px solid ${T.border}`, padding: '16px 18px' }}>
                                            {/* Platform icon + author on right like screenshot */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                <div style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <span style={{ color: platColor, fontSize: 16 }}>{post.source === 'twitter' ? '𝕏' : post.source === 'telegram' ? '✈' : post.source === 'tiktok' ? '♪' : '📷'}</span>
                                                </div>
                                                <div style={{ flex: 1 }} />
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ textAlign: isRTL ? 'left' : 'right' }}>
                                                        <div style={{ color: T.textPrimary, fontSize: 13, fontWeight: 600 }}>{post.author_name}</div>
                                                        <div style={{ color: T.textMuted, fontSize: 11 }}>@{post.username}</div>
                                                    </div>
                                                    <div style={{ width: 34, height: 34, borderRadius: 17, background: `${platColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <span style={{ fontSize: 14, fontWeight: 700, color: platColor }}>{post.author_name?.[0] || '?'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Tweet text */}
                                            <p style={{ margin: '0 0 12px', color: T.textPrimary, fontSize: 14, lineHeight: '22px', textAlign }}>{post.tweet_content}</p>
                                            {/* View on X link */}
                                            <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                                                <span style={{ color: '#0a84ff', fontSize: 12, fontWeight: 500 }}>{isRTL ? 'צפה ב-X' : 'View on X'} ↗</span>
                                            </div>
                                        </div>

                                        {/* Summary + Why included — like screenshot 2 bottom section */}
                                        <div style={{ padding: '0 20px 18px' }}>
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, marginBottom: 4, textAlign }}>{isRTL ? 'סיכום' : 'Summary'}</div>
                                                <p style={{ margin: 0, color: T.textSecondary, fontSize: 13, lineHeight: '20px', textAlign }}>{post.summary}</p>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, marginBottom: 4, textAlign }}>{isRTL ? 'למה הפוסט הזה נכלל' : 'Why this post was included'}</div>
                                                <p style={{ margin: 0, color: T.textSecondary, fontSize: 13, lineHeight: '20px', textAlign }}>{post.reason}</p>
                                            </div>
                                        </div>

                                        {/* Bottom action bar — like screenshot 2 buttons */}
                                        <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 8, justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                                            <span style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'rgba(10,132,255,0.1)', color: '#0a84ff', border: '1px solid rgba(10,132,255,0.25)' }}>{isRTL ? 'פתח' : 'Open'}</span>
                                            <span style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'rgba(10,132,255,0.1)', color: '#0a84ff', border: '1px solid rgba(10,132,255,0.25)' }}>{isRTL ? 'שתף' : 'Share'}</span>
                                            <span style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'rgba(10,132,255,0.1)', color: '#0a84ff', border: '1px solid rgba(10,132,255,0.25)' }}>{isRTL ? 'תרגם' : 'Translate'}</span>
                                            <span style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#30d158', color: '#fff' }}>{isRTL ? 'תגובה' : 'Reply'}</span>
                                            <span style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'rgba(10,132,255,0.1)', color: '#0a84ff', border: '1px solid rgba(10,132,255,0.25)' }}>{isRTL ? 'מעקב' : 'Track'}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ═══ Section 3: Trending Narrative — like screenshot 3+4 ═══ */}
                    {result.narrative && (
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted, marginBottom: 14, textAlign }}>{isRTL ? 'נרטיב מגמתי' : 'TRENDING NARRATIVE'}</div>

                            {/* Two-column: left = posts feed, right = narrative summary (like screenshot 3) */}
                            <div style={{ display: 'flex', flexDirection: isRTL ? 'row-reverse' : 'row', gap: 16, borderRadius: 14, background: T.bgCard, border: `1px solid ${T.border}`, overflow: 'hidden' }}>

                                {/* LEFT: sample posts feed */}
                                <div style={{ flex: 1, padding: '18px 20px', borderRight: isRTL ? undefined : `1px solid ${T.border}`, borderLeft: isRTL ? `1px solid ${T.border}` : undefined }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {result.narrative.sample_posts?.map((sp, i) => {
                                            const spPlatColor = DEMO_PLATFORM_COLORS[sp.source] || '#8e8e93';
                                            return (
                                                <div key={i} style={{ borderRadius: 12, background: T.bg, border: `1px solid ${T.border}`, padding: '14px 16px', borderRight: isRTL ? `3px solid #0a84ff` : undefined, borderLeft: isRTL ? undefined : `3px solid #0a84ff` }}>
                                                    {/* Velocity + time */}
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                        <span style={{ fontSize: 10, color: T.textMuted }}>1d ago</span>
                                                        <span style={{ fontSize: 10, fontWeight: 700, color: '#0a84ff' }}>{isRTL ? 'פעיל' : 'Active'} X 0.0</span>
                                                    </div>
                                                    {/* Post text as title */}
                                                    <p style={{ margin: '0 0 8px', color: T.textPrimary, fontSize: 14, fontWeight: 600, lineHeight: '21px', textAlign }}>{sp.tweet_content}</p>
                                                    {/* Author */}
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <span style={{ fontSize: 11, color: T.textMuted }}>@{sp.username} · {sp.source}</span>
                                                        <span style={{ fontSize: 11, color: T.textMuted }}>💬 {sp.comment_count || 0}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* RIGHT: narrative summary + key point (like screenshot 3 right panel) */}
                                <div style={{ width: 340, flexShrink: 0, padding: '18px 20px' }}>
                                    {/* Title + trending badge */}
                                    <div style={{ marginBottom: 14 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <FaFire size={14} color="#ff453a" />
                                            <span style={{ padding: '3px 9px', borderRadius: 10, fontSize: 9, fontWeight: 800, textTransform: 'uppercase', background: 'rgba(255,69,58,0.12)', color: '#ff453a' }}>TRENDING</span>
                                        </div>
                                        <h3 style={{ margin: '0 0 10px', color: T.textPrimary, fontSize: 17, fontWeight: 700, lineHeight: '24px', textAlign }}>{result.narrative.title}</h3>
                                    </div>
                                    {/* Summary */}
                                    <p style={{ margin: '0 0 16px', color: T.textSecondary, fontSize: 13, lineHeight: '21px', textAlign }}>{result.narrative.summary}</p>
                                    {/* Key point section — like "נקודת מפתח" in screenshot 3 */}
                                    <div style={{ borderRadius: 10, background: T.bg, border: `1px solid ${T.border}`, padding: '12px 14px' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#0a84ff', marginBottom: 6, textAlign }}>{isRTL ? 'נקודת מפתח' : 'Key Point'}</div>
                                        <p style={{ margin: 0, color: T.textSecondary, fontSize: 13, lineHeight: '20px', textAlign }}>{result.narrative.representative_text}</p>
                                    </div>
                                    {/* Engagement */}
                                    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 16 }}>
                                        <span style={{ fontSize: 11, color: T.textMuted }}>{isRTL ? 'מעורבות' : 'Engagement'}: {result.narrative.engagement_score?.toLocaleString()}</span>
                                        <span style={{ fontSize: 11, color: T.textMuted }}>{isRTL ? 'פוסטים' : 'Posts'}: {result.narrative.sample_posts?.length || 0}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </View>
    );
}


function AdminOrgTags({ orgId }) {
    const [tags, setTags] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [loading, setLoading] = useState(false);

    const loadTags = useCallback(() => {
        OrgManager.listTags(orgId).then(setTags).catch(() => setTags([]));
    }, [orgId]);

    useEffect(() => { loadTags(); }, [loadTags]);

    const handleAdd = async () => {
        if (!newTag.trim()) return;
        setLoading(true);
        try {
            await OrgManager.createTag(orgId, newTag.trim());
            setNewTag('');
            loadTags();
        } catch (e) {
            alert(`Failed: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (tagId) => {
        try {
            await OrgManager.deleteTag(orgId, tagId);
            loadTags();
        } catch (e) {
            alert(`Failed: ${e.message}`);
        }
    };

    return (
        <View style={{ marginTop: 16 }}>
            <Text style={{ color: THEME.textPrimary, fontSize: 13, fontWeight: '700', marginBottom: 8 }}>Tags</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {tags.map(tag => (
                    <View key={tag.id} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(77,166,255,0.1)', borderRadius: 14, paddingHorizontal: 10, paddingVertical: 5, gap: 6 }}>
                        <Text style={{ color: THEME.textPrimary, fontSize: 12 }}>{tag.name}</Text>
                        <TouchableOpacity onPress={() => handleDelete(tag.id)}>
                            <FaTimes size={9} color="#ff4444" />
                        </TouchableOpacity>
                    </View>
                ))}
                {tags.length === 0 && <Text style={{ color: THEME.textMuted, fontSize: 12 }}>No tags</Text>}
            </View>
            <View style={{ flexDirection: 'row', gap: 6 }}>
                <TextInput
                    style={{ flex: 1, backgroundColor: THEME.bg, color: THEME.textPrimary, borderWidth: 1, borderColor: THEME.border, borderRadius: 6, paddingHorizontal: 10, paddingVertical: 6, fontSize: 12 }}
                    value={newTag}
                    onChangeText={setNewTag}
                    placeholder="Add tag..."
                    placeholderTextColor="#666"
                    onSubmitEditing={handleAdd}
                />
                <TouchableOpacity onPress={handleAdd} disabled={loading || !newTag.trim()} style={{ backgroundColor: '#4DA6FF', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, opacity: loading || !newTag.trim() ? 0.5 : 1 }}>
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Add</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
}

function TaggerImageCard({ img, tag, onTag, theme, bbox, onClickImage, selected, onSelect }) {
    const borderColor = selected ? '#60a5fa' : tag === 'approved' ? '#22c55e' : tag === 'rejected' ? '#ff4444' : theme.border;
    const hasBbox = bbox && bbox.visible;

    return (
        <div style={{ border: `3px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden', background: theme.bg, transition: 'border-color 0.2s', boxShadow: selected ? '0 0 8px rgba(96,165,250,0.5)' : 'none' }}>
            <div
                style={{ width: '100%', height: 140, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', position: 'relative', cursor: 'pointer' }}
                onClick={(e) => {
                    if (e.shiftKey || e.metaKey || e.ctrlKey) {
                        onSelect && onSelect(img, e);
                    } else {
                        onClickImage && onClickImage(img);
                    }
                }}
            >
                {img.url && <img src={img.url} alt={img.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                {hasBbox && (
                    <div style={{
                        position: 'absolute',
                        left: `${(bbox.x_center - bbox.width / 2) * 100}%`,
                        top: `${(bbox.y_center - bbox.height / 2) * 100}%`,
                        width: `${bbox.width * 100}%`,
                        height: `${bbox.height * 100}%`,
                        border: '2px solid #00ff88',
                        borderRadius: 2,
                        pointerEvents: 'none',
                        boxShadow: '0 0 4px rgba(0,255,136,0.5)',
                    }} />
                )}
                {hasBbox && (
                    <div style={{ position: 'absolute', top: 4, right: 4, background: '#00ff88', color: '#000', fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3 }}>
                        bbox
                    </div>
                )}
                {selected && (
                    <div style={{ position: 'absolute', top: 4, left: 4, background: '#60a5fa', color: '#fff', fontSize: 10, fontWeight: 700, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✓
                    </div>
                )}
            </div>
            <div style={{ padding: 8, display: 'flex', justifyContent: 'center', gap: 6 }}>
                <button
                    onClick={() => onTag(img.filename, tag === 'approved' ? 'untagged' : 'approved')}
                    style={{ background: tag === 'approved' ? '#22c55e' : 'rgba(34,197,94,0.15)', color: tag === 'approved' ? '#fff' : '#22c55e', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                    Yes
                </button>
                <button
                    onClick={() => onTag(img.filename, tag === 'rejected' ? 'untagged' : 'rejected')}
                    style={{ background: tag === 'rejected' ? '#ff4444' : 'rgba(255,68,68,0.15)', color: tag === 'rejected' ? '#fff' : '#ff4444', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                    No
                </button>
            </div>
            <div style={{ padding: '0 8px 6px', textAlign: 'center' }}>
                <span style={{ fontSize: 10, color: theme.textMuted }}>{img.filename.length > 18 ? img.filename.slice(0, 18) + '...' : img.filename}</span>
            </div>
        </div>
    );
}

function BBoxEditorModal({ img, bbox, onSave, onClose, theme }) {
    const canvasRef = React.useRef(null);
    const imgRef = React.useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [startPos, setStartPos] = useState(null);
    const [currentBox, setCurrentBox] = useState(bbox && bbox.visible ? bbox : null);
    const [imgLoaded, setImgLoaded] = useState(false);

    const drawOverlay = useCallback(() => {
        const canvas = canvasRef.current;
        const image = imgRef.current;
        if (!canvas || !image || !imgLoaded) return;

        const ctx = canvas.getContext('2d');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        canvas.style.width = '100%';
        canvas.style.height = '100%';

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (currentBox) {
            const x = (currentBox.x_center - currentBox.width / 2) * canvas.width;
            const y = (currentBox.y_center - currentBox.height / 2) * canvas.height;
            const w = currentBox.width * canvas.width;
            const h = currentBox.height * canvas.height;

            ctx.strokeStyle = '#00ff88';
            ctx.lineWidth = 3;
            ctx.strokeRect(x, y, w, h);
            ctx.fillStyle = 'rgba(0, 255, 136, 0.1)';
            ctx.fillRect(x, y, w, h);

            ctx.fillStyle = '#00ff88';
            ctx.font = '14px monospace';
            ctx.fillText(
                `(${currentBox.x_center.toFixed(3)}, ${currentBox.y_center.toFixed(3)}, ${currentBox.width.toFixed(3)}, ${currentBox.height.toFixed(3)})`,
                x, y - 6
            );
        }
    }, [currentBox, imgLoaded]);

    useEffect(() => { drawOverlay(); }, [drawOverlay]);

    const getRelativePos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) / rect.width,
            y: (e.clientY - rect.top) / rect.height,
        };
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        const pos = getRelativePos(e);
        setDrawing(true);
        setStartPos(pos);
    };

    const handleMouseMove = (e) => {
        if (!drawing || !startPos) return;
        const pos = getRelativePos(e);
        const x1 = Math.min(startPos.x, pos.x);
        const y1 = Math.min(startPos.y, pos.y);
        const x2 = Math.max(startPos.x, pos.x);
        const y2 = Math.max(startPos.y, pos.y);
        setCurrentBox({
            x_center: (x1 + x2) / 2,
            y_center: (y1 + y2) / 2,
            width: x2 - x1,
            height: y2 - y1,
            visible: true,
        });
    };

    const handleMouseUp = () => {
        setDrawing(false);
        setStartPos(null);
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10001, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: theme.bgCard, borderRadius: 12, padding: 20, maxWidth: '85vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', gap: 12, border: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: theme.textPrimary, fontSize: 14, fontWeight: 600 }}>
                        Edit BBox: {img.filename}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={() => setCurrentBox(null)}
                            style={{ background: 'rgba(255,68,68,0.15)', color: '#ff4444', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                            Remove Box
                        </button>
                        <button
                            onClick={() => { onSave(img.filename, currentBox); onClose(); }}
                            style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            Save
                        </button>
                        <button
                            onClick={onClose}
                            style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '8px 16px', color: theme.textPrimary, fontSize: 13, cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                <div style={{ color: theme.textMuted, fontSize: 11 }}>
                    Draw a rectangle on the image to set the bounding box. Click "Remove Box" to mark as not visible.
                </div>
                <div style={{ position: 'relative', maxHeight: '70vh', overflow: 'hidden', display: 'inline-block' }}>
                    <img
                        ref={imgRef}
                        src={img.url}
                        alt={img.filename}
                        onLoad={() => setImgLoaded(true)}
                        style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block' }}
                        draggable={false}
                    />
                    <canvas
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: 'crosshair' }}
                    />
                </div>
            </div>
        </div>
    );
}

function ProductDetectionTab() {
    const { theme: THEME } = useTheme();
    const [products, setProducts] = useState([]);
    const [detections, setDetections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [orgs, setOrgs] = useState([]);
    const [lists, setLists] = useState([]);
    const [trainingStatus, setTrainingStatus] = useState(null);
    const [scrapeStatus, setScrapeStatus] = useState({});

    const [form, setForm] = useState({
        orgId: '', listId: '', brandName: '', productName: '', description: '', searchTerms: '',
    });
    const [editProduct, setEditProduct] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editLists, setEditLists] = useState([]);
    const [editLoading, setEditLoading] = useState(false);

    const [taggerProduct, setTaggerProduct] = useState(null);
    const [taggerImages, setTaggerImages] = useState(null);
    const [taggerLoading, setTaggerLoading] = useState(false);
    const [taggerIndex, setTaggerIndex] = useState(0);
    const [taggerTags, setTaggerTags] = useState({});
    const [taggerBBoxes, setTaggerBBoxes] = useState({});
    const [bboxEditorImg, setBboxEditorImg] = useState(null);
    const [annotateStatus, setAnnotateStatus] = useState(null);

    const fetchProducts = useCallback(async () => {
        try {
            const data = await AdminManager.getProducts();
            setProducts(data);
        } catch (e) { console.error(e); }
    }, []);

    const fetchDetections = useCallback(async () => {
        try {
            const data = await AdminManager.getRecentDetections(null, 30);
            setDetections(data);
        } catch (e) { console.error(e); }
    }, []);

    const fetchOrgs = useCallback(async () => {
        try {
            const { default: OrgManager } = await import('../services/OrgManager');
            const data = await AdminManager.getAllListeners();
            // fetch orgs from admin endpoint
            const orgsResp = await fetch(`${AdminManager.baseUrl}/admin/orgs`, {
                headers: await AdminManager.getAuthHeaders(),
            });
            const orgsData = await orgsResp.json();
            setOrgs(orgsData);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchDetections();
        fetchOrgs();
    }, []);

    const handleOrgChange = async (orgId) => {
        setForm(f => ({ ...f, orgId, listId: '' }));
        if (!orgId) { setLists([]); return; }
        try {
            const headers = await AdminManager.getAuthHeaders();
            const resp = await fetch(`${AdminManager.baseUrl}/admin/orgs/${orgId}`, { headers });
            const org = await resp.json();
            // Fetch lists for this org
            const listsResp = await fetch(`${AdminManager.baseUrl}/lists?org_id=${orgId}`, { headers });
            if (listsResp.ok) {
                const listsData = await listsResp.json();
                setLists(Array.isArray(listsData) ? listsData : listsData.lists || []);
            }
        } catch (e) { console.error(e); setLists([]); }
    };

    const handleCreate = async () => {
        if (!form.orgId || !form.listId || !form.brandName || !form.productName || !form.searchTerms) return;
        setLoading(true);
        try {
            await AdminManager.createProduct({
                orgId: form.orgId,
                listId: form.listId,
                brandName: form.brandName,
                productName: form.productName,
                description: form.description,
                searchTerms: form.searchTerms.split(',').map(s => s.trim()).filter(Boolean),
            });
            setForm({ orgId: '', listId: '', brandName: '', productName: '', description: '', searchTerms: '' });
            setShowForm(false);
            fetchProducts();
        } catch (e) {
            alert(e.response?.data?.detail || e.message);
        }
        setLoading(false);
    };

    const handleScrape = async (productId) => {
        setScrapeStatus(s => ({ ...s, [productId]: 'scraping...' }));
        try {
            const result = await AdminManager.scrapeProductImages(productId);
            setScrapeStatus(s => ({ ...s, [productId]: `${result.images_downloaded} images` }));
            fetchProducts();
        } catch (e) {
            setScrapeStatus(s => ({ ...s, [productId]: 'failed' }));
        }
    };

    const [socialScrapeStatus, setSocialScrapeStatus] = useState({});
    const handleSocialScrape = async (productId) => {
        setSocialScrapeStatus(s => ({ ...s, [productId]: 'scraping social...' }));
        try {
            const result = await AdminManager.scrapeSocialImages(productId);
            const details = Object.entries(result.images_per_platform || {}).map(([k,v]) => `${k}:${v}`).join(' ');
            setSocialScrapeStatus(s => ({ ...s, [productId]: `${result.total} imgs (${details})` }));
            fetchProducts();
        } catch (e) {
            setSocialScrapeStatus(s => ({ ...s, [productId]: 'failed' }));
        }
    };

    const handleTrain = async () => {
        setTrainingStatus('Submitting training job...');
        try {
            const result = await AdminManager.rebuildAndTrain();
            if (result.job_id) {
                setTrainingStatus(`Training queued (job: ${result.job_id.slice(0, 8)}...). Polling...`);
                let pollCount = 0;
                const maxPolls = 120; // 10 minutes at 5s intervals
                const pollInterval = setInterval(async () => {
                    pollCount++;
                    if (pollCount >= maxPolls) {
                        clearInterval(pollInterval);
                        setTrainingStatus('Training timed out (10 min). Check server logs.');
                        return;
                    }
                    try {
                        const status = await AdminManager.getTrainingStatus(result.job_id);
                        if (status.status === 'completed') {
                            setTrainingStatus(`Done! v${status.model_version} (${status.num_classes} classes)`);
                            clearInterval(pollInterval);
                            fetchProducts();
                        } else if (status.status === 'failed') {
                            setTrainingStatus(`Error: ${status.error_message || 'Training failed'}`);
                            clearInterval(pollInterval);
                        } else {
                            setTrainingStatus(`Training in progress... (${status.status})`);
                        }
                    } catch (pollErr) {
                        clearInterval(pollInterval);
                        setTrainingStatus(`Error polling status: ${pollErr.message}`);
                    }
                }, 5000);
            } else {
                setTrainingStatus(`Done! v${result.model_version} (${result.num_classes} classes)`);
                fetchProducts();
            }
        } catch (e) {
            setTrainingStatus(`Error: ${e.response?.data?.detail || e.message}`);
        }
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Deactivate this product?')) return;
        await AdminManager.deleteProduct(productId);
        fetchProducts();
    };

    const openEditModal = async (product) => {
        setEditProduct(product);
        setEditForm({
            orgId: product.org_id,
            listId: product.list_id,
            brandName: product.brand_name,
            productName: product.product_name,
            description: product.description || '',
            searchTerms: (product.search_terms || []).join(', '),
        });
        try {
            const headers = await AdminManager.getAuthHeaders();
            const listsResp = await fetch(`${AdminManager.baseUrl}/lists?org_id=${product.org_id}`, { headers });
            if (listsResp.ok) {
                const listsData = await listsResp.json();
                setEditLists(Array.isArray(listsData) ? listsData : listsData.lists || []);
            }
        } catch (e) { setEditLists([]); }
    };

    const handleEditOrgChange = async (orgId) => {
        setEditForm(f => ({ ...f, orgId, listId: '' }));
        if (!orgId) { setEditLists([]); return; }
        try {
            const headers = await AdminManager.getAuthHeaders();
            const listsResp = await fetch(`${AdminManager.baseUrl}/lists?org_id=${orgId}`, { headers });
            if (listsResp.ok) {
                const listsData = await listsResp.json();
                setEditLists(Array.isArray(listsData) ? listsData : listsData.lists || []);
            }
        } catch (e) { setEditLists([]); }
    };

    const handleEditSave = async () => {
        if (!editProduct) return;
        setEditLoading(true);
        try {
            const updates = {};
            if (editForm.orgId !== editProduct.org_id) updates.org_id = editForm.orgId;
            if (editForm.listId !== editProduct.list_id) updates.list_id = editForm.listId;
            if (editForm.brandName !== editProduct.brand_name) updates.brand_name = editForm.brandName;
            if (editForm.productName !== editProduct.product_name) updates.product_name = editForm.productName;
            if (editForm.description !== (editProduct.description || '')) updates.description = editForm.description;
            const newTerms = editForm.searchTerms.split(',').map(s => s.trim()).filter(Boolean);
            if (JSON.stringify(newTerms) !== JSON.stringify(editProduct.search_terms || [])) updates.search_terms = newTerms;

            if (Object.keys(updates).length === 0) {
                setEditProduct(null);
                setEditLoading(false);
                return;
            }
            await AdminManager.updateProduct(editProduct.id, updates);
            setEditProduct(null);
            fetchProducts();
        } catch (e) {
            alert('Failed to update: ' + (e.response?.data?.detail || e.message));
        }
        setEditLoading(false);
    };

    const openTagger = async (product) => {
        setTaggerProduct(product);
        setTaggerLoading(true);
        setTaggerIndex(0);
        setAnnotateStatus(null);
        try {
            const [data, bboxData] = await Promise.all([
                AdminManager.getProductImages(product.id),
                AdminManager.getProductBBoxes(product.id).catch(() => ({ bboxes: {} })),
            ]);
            console.log('🖼️ Tagger images response:', JSON.stringify(data.images?.slice(0, 3), null, 2));
            setTaggerImages(data);
            const existingTags = {};
            data.images.forEach(img => { existingTags[img.filename] = img.tag; });
            setTaggerTags(existingTags);
            setTaggerBBoxes(bboxData.bboxes || {});
        } catch (e) {
            alert('Failed to load images: ' + (e.response?.data?.detail || e.message));
            setTaggerProduct(null);
        }
        setTaggerLoading(false);
    };

    const handleTag = async (filename, tag) => {
        const newTags = { ...taggerTags, [filename]: tag };
        setTaggerTags(newTags);
        try {
            await AdminManager.tagProductImages(taggerProduct.id, { [filename]: tag });
        } catch (e) { console.error('Tag failed:', e); }
    };

    const handleTagAll = async (tag) => {
        if (!taggerImages) return;
        const untagged = taggerImages.images.filter(img => taggerTags[img.filename] === 'untagged' || !taggerTags[img.filename]);
        const batch = {};
        untagged.forEach(img => { batch[img.filename] = tag; });
        const newTags = { ...taggerTags, ...batch };
        setTaggerTags(newTags);
        try {
            await AdminManager.tagProductImages(taggerProduct.id, batch);
        } catch (e) { console.error('Batch tag failed:', e); }
    };

    const handleDeleteRejected = async () => {
        const rejectedCount = Object.values(taggerTags).filter(t => t === 'rejected').length;
        if (!rejectedCount || !window.confirm(`Permanently delete ${rejectedCount} rejected images from S3?`)) return;
        try {
            await AdminManager.deleteRejectedImages(taggerProduct.id);
            await openTagger(taggerProduct);
        } catch (e) {
            alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
        }
    };

    const closeTagger = () => {
        setTaggerProduct(null);
        setTaggerImages(null);
        setTaggerTags({});
        setTaggerBBoxes({});
        setTaggerIndex(0);
        setBboxEditorImg(null);
        setAnnotateStatus(null);
        setSelectedImages(new Set());
        fetchProducts();
    };

    const [selectedImages, setSelectedImages] = useState(new Set());
    const lastSelectedRef = React.useRef(null);

    const handleSelectImage = (img, event) => {
        const newSelected = new Set(selectedImages);
        if (event.shiftKey && lastSelectedRef.current && taggerImages) {
            const allFilenames = taggerImages.images.map(i => i.filename);
            const lastIdx = allFilenames.indexOf(lastSelectedRef.current);
            const currIdx = allFilenames.indexOf(img.filename);
            if (lastIdx >= 0 && currIdx >= 0) {
                const [start, end] = lastIdx < currIdx ? [lastIdx, currIdx] : [currIdx, lastIdx];
                for (let i = start; i <= end; i++) {
                    newSelected.add(allFilenames[i]);
                }
            }
        } else {
            if (newSelected.has(img.filename)) {
                newSelected.delete(img.filename);
            } else {
                newSelected.add(img.filename);
            }
        }
        lastSelectedRef.current = img.filename;
        setSelectedImages(newSelected);
    };

    const handleBulkTag = async (tag) => {
        if (selectedImages.size === 0) return;
        const batch = {};
        selectedImages.forEach(filename => { batch[filename] = tag; });
        const newTags = { ...taggerTags, ...batch };
        setTaggerTags(newTags);
        setSelectedImages(new Set());
        try {
            await AdminManager.tagProductImages(taggerProduct.id, batch);
        } catch (e) { console.error('Bulk tag failed:', e); }
    };

    const handleAutoAnnotate = async () => {
        if (!taggerProduct) return;
        setAnnotateStatus('Running Gemini auto-annotation...');
        try {
            const result = await AdminManager.autoAnnotateProduct(taggerProduct.id);
            setAnnotateStatus(`Done: ${result.visible} visible boxes out of ${result.total} images`);
            const bboxData = await AdminManager.getProductBBoxes(taggerProduct.id);
            setTaggerBBoxes(bboxData.bboxes || {});
        } catch (e) {
            setAnnotateStatus('Error: ' + (e.response?.data?.detail || e.message));
        }
    };

    const handleSaveBBox = async (filename, box) => {
        const newBBoxes = { ...taggerBBoxes };
        if (box) {
            newBBoxes[filename] = box;
        } else {
            newBBoxes[filename] = { x_center: 0, y_center: 0, width: 0, height: 0, visible: false };
        }
        setTaggerBBoxes(newBBoxes);
        try {
            await AdminManager.saveProductBBoxes(taggerProduct.id, newBBoxes);
        } catch (e) {
            console.error('Failed to save bbox:', e);
        }
    };

    const cardStyle = { background: THEME.bgCard, borderRadius: 12, padding: 20, marginBottom: 16, border: `1px solid ${THEME.border}` };
    const inputStyle = { background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: '10px 14px', color: THEME.textPrimary, fontSize: 14, width: '100%' };
    const btnPrimary = { background: '#4DA6FF', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
    const btnSecondary = { background: 'rgba(77,166,255,0.15)', color: '#4DA6FF', border: '1px solid rgba(77,166,255,0.3)', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
    const btnDanger = { background: 'rgba(255,68,68,0.15)', color: '#ff4444', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' };

    return (
        <View style={{ padding: 24 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <View>
                    <Text style={{ fontSize: 22, fontWeight: 'bold', color: THEME.textPrimary }}>Product Detection</Text>
                    <Text style={{ fontSize: 13, color: THEME.textMuted, marginTop: 4 }}>Manage YOLO26 product classes for story detection</Text>
                </View>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                    <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>
                        {showForm ? 'Cancel' : '+ Add Product'}
                    </button>
                    <button onClick={handleTrain} style={{ ...btnPrimary, background: '#22c55e' }}>
                        Rebuild & Train
                    </button>
                </View>
            </View>

            {trainingStatus && (
                <div style={{ ...cardStyle, background: trainingStatus.startsWith('Error') ? 'rgba(255,68,68,0.1)' : 'rgba(34,197,94,0.1)', borderColor: trainingStatus.startsWith('Error') ? '#ff4444' : '#22c55e' }}>
                    <Text style={{ color: trainingStatus.startsWith('Error') ? '#ff4444' : '#22c55e', fontSize: 14, fontWeight: 600 }}>
                        {trainingStatus}
                    </Text>
                </div>
            )}

            {showForm && (
                <div style={cardStyle}>
                    <Text style={{ fontSize: 16, fontWeight: 600, color: THEME.textPrimary, marginBottom: 16 }}>Add New Product</Text>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Organization *</Text>
                            <select value={form.orgId} onChange={e => handleOrgChange(e.target.value)} style={inputStyle}>
                                <option value="">Select org...</option>
                                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>List *</Text>
                            <select value={form.listId} onChange={e => setForm(f => ({ ...f, listId: e.target.value }))} style={inputStyle}>
                                <option value="">Select list...</option>
                                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Brand Name *</Text>
                            <input value={form.brandName} onChange={e => setForm(f => ({ ...f, brandName: e.target.value }))} placeholder="e.g. Starkist" style={inputStyle} />
                        </div>
                        <div>
                            <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Product Name *</Text>
                            <input value={form.productName} onChange={e => setForm(f => ({ ...f, productName: e.target.value }))} placeholder="e.g. tuna" style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Search Terms * (comma-separated)</Text>
                            <input value={form.searchTerms} onChange={e => setForm(f => ({ ...f, searchTerms: e.target.value }))} placeholder="טונה סטארקיסט, starkist tuna can, starkist tuna israel" style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Description (optional)</Text>
                            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Starkist tuna cans - all varieties" style={inputStyle} />
                        </div>
                    </div>
                    <button onClick={handleCreate} disabled={loading} style={{ ...btnPrimary, marginTop: 16, opacity: loading ? 0.6 : 1 }}>
                        {loading ? 'Creating...' : 'Create Product'}
                    </button>
                </div>
            )}

            {/* Products Table */}
            <div style={cardStyle}>
                <Text style={{ fontSize: 16, fontWeight: 600, color: THEME.textPrimary, marginBottom: 12 }}>
                    Active Products ({products.length})
                </Text>
                {products.length === 0 ? (
                    <Text style={{ color: THEME.textMuted, fontSize: 14 }}>No products configured yet. Add one above.</Text>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '34%' }} />
                            </colgroup>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${THEME.border}` }}>
                                    {['Org', 'Brand', 'Product', 'List', 'Images', 'Status', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.id} style={{ borderBottom: `1px solid ${THEME.border}22` }}>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: THEME.textPrimary }}>{p.org_name}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: THEME.textPrimary, fontWeight: 600 }}>{p.brand_name}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: THEME.textPrimary }}>{p.product_name}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: THEME.textMuted }}>{p.list_name}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: p.image_count > 0 ? '#22c55e' : THEME.textMuted }}>{p.image_count || 0}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: p.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(255,68,68,0.15)', color: p.is_active ? '#22c55e' : '#ff4444' }}>
                                                {p.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                                                <button onClick={() => openEditModal(p)} style={{ ...btnSecondary, background: 'rgba(255,165,0,0.15)', color: '#ffa500', borderColor: 'rgba(255,165,0,0.3)' }}>
                                                    Edit
                                                </button>
                                                <button onClick={() => handleScrape(p.id)} style={btnSecondary}>
                                                    {scrapeStatus[p.id] || 'Scrape'}
                                                </button>
                                                <button onClick={() => handleSocialScrape(p.id)} disabled={socialScrapeStatus[p.id] === 'scraping social...'} style={{ ...btnSecondary, background: 'rgba(59,130,246,0.15)', color: '#3b82f6', borderColor: 'rgba(59,130,246,0.3)', opacity: socialScrapeStatus[p.id] === 'scraping social...' ? 0.5 : 1 }}>
                                                    {socialScrapeStatus[p.id] || 'Social'}
                                                </button>
                                                <button onClick={() => openTagger(p)} style={{ ...btnSecondary, background: 'rgba(168,85,247,0.15)', color: '#a855f7', borderColor: 'rgba(168,85,247,0.3)' }}>
                                                    Tag
                                                </button>
                                                <button onClick={() => handleDelete(p.id)} style={btnDanger}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Product Modal */}
            {editProduct && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: THEME.bgCard, borderRadius: 16, padding: 24, width: '90vw', maxWidth: 600, border: `1px solid ${THEME.border}` }}>
                        <Text style={{ fontSize: 18, fontWeight: 700, color: THEME.textPrimary, marginBottom: 20 }}>
                            Edit Product: {editProduct.brand_name} / {editProduct.product_name}
                        </Text>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Organization</Text>
                                <select value={editForm.orgId} onChange={e => handleEditOrgChange(e.target.value)} style={inputStyle}>
                                    <option value="">Select org...</option>
                                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>List</Text>
                                <select value={editForm.listId} onChange={e => setEditForm(f => ({ ...f, listId: e.target.value }))} style={inputStyle}>
                                    <option value="">Select list...</option>
                                    {editLists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Brand Name</Text>
                                <input value={editForm.brandName} onChange={e => setEditForm(f => ({ ...f, brandName: e.target.value }))} style={inputStyle} />
                            </div>
                            <div>
                                <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Product Name</Text>
                                <input value={editForm.productName} onChange={e => setEditForm(f => ({ ...f, productName: e.target.value }))} style={inputStyle} />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Search Terms (comma-separated)</Text>
                                <input value={editForm.searchTerms} onChange={e => setEditForm(f => ({ ...f, searchTerms: e.target.value }))} style={inputStyle} />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <Text style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 4 }}>Description</Text>
                                <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                            <button onClick={() => setEditProduct(null)} style={{ background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: '10px 20px', color: THEME.textPrimary, fontSize: 14, cursor: 'pointer' }}>
                                Cancel
                            </button>
                            <button onClick={handleEditSave} disabled={editLoading} style={{ ...btnPrimary, opacity: editLoading ? 0.6 : 1 }}>
                                {editLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Tagger Modal */}
            {taggerProduct && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: THEME.bgCard, borderRadius: 16, padding: 24, width: '90vw', maxWidth: 900, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${THEME.border}` }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <Text style={{ fontSize: 18, fontWeight: 700, color: THEME.textPrimary }}>
                                    Tag Images: {taggerProduct.brand_name} / {taggerProduct.product_name}
                                </Text>
                                {taggerImages && (
                                    <Text style={{ fontSize: 13, color: THEME.textMuted, marginTop: 4 }}>
                                        {Object.values(taggerTags).filter(t => t === 'approved').length} approved / {Object.values(taggerTags).filter(t => t === 'rejected').length} rejected / {taggerImages.total} total
                                    </Text>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button onClick={() => window.open(`/admin/annotate/${taggerProduct.id}`, '_blank')} style={{ ...btnSecondary, background: 'rgba(0,255,136,0.12)', color: '#00ff88', borderColor: 'rgba(0,255,136,0.3)' }}>
                                    Open Annotator
                                </button>
                                <button onClick={handleAutoAnnotate} style={{ ...btnSecondary, background: 'rgba(255,165,0,0.15)', color: '#ffa500', borderColor: 'rgba(255,165,0,0.3)' }}>
                                    Auto-Annotate BBoxes
                                </button>
                                <button onClick={() => handleTagAll('approved')} style={{ ...btnSecondary, background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }}>
                                    Approve All Untagged
                                </button>
                                {Object.values(taggerTags).filter(t => t === 'rejected').length > 0 && (
                                    <button onClick={handleDeleteRejected} style={btnDanger}>
                                        Delete Rejected ({Object.values(taggerTags).filter(t => t === 'rejected').length})
                                    </button>
                                )}
                                <button onClick={closeTagger} style={{ background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: '8px 16px', color: THEME.textPrimary, fontSize: 14, cursor: 'pointer' }}>
                                    Done
                                </button>
                            </div>
                        </div>

                        {annotateStatus && (
                            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: annotateStatus.startsWith('Error') ? 'rgba(255,68,68,0.1)' : annotateStatus.startsWith('Running') ? 'rgba(255,165,0,0.1)' : 'rgba(34,197,94,0.1)', border: `1px solid ${annotateStatus.startsWith('Error') ? '#ff4444' : annotateStatus.startsWith('Running') ? '#ffa500' : '#22c55e'}` }}>
                                <span style={{ fontSize: 12, color: annotateStatus.startsWith('Error') ? '#ff4444' : annotateStatus.startsWith('Running') ? '#ffa500' : '#22c55e' }}>
                                    {annotateStatus}
                                </span>
                            </div>
                        )}

                        {selectedImages.size > 0 && (
                            <div style={{ marginBottom: 12, padding: '10px 16px', borderRadius: 8, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ fontSize: 13, color: '#60a5fa', fontWeight: 600 }}>{selectedImages.size} selected</span>
                                <button onClick={() => handleBulkTag('approved')} style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                    Approve All
                                </button>
                                <button onClick={() => handleBulkTag('rejected')} style={{ background: '#ff4444', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                    Reject All
                                </button>
                                <button onClick={() => setSelectedImages(new Set())} style={{ background: 'transparent', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>
                                    Clear
                                </button>
                                <span style={{ fontSize: 11, color: THEME.textMuted, marginLeft: 'auto' }}>Shift+click to select range</span>
                            </div>
                        )}

                        {taggerLoading ? (
                            <Text style={{ color: THEME.textMuted, textAlign: 'center', padding: 40 }}>Loading images...</Text>
                        ) : taggerImages && taggerImages.images.length === 0 ? (
                            <Text style={{ color: THEME.textMuted, textAlign: 'center', padding: 40 }}>No images found. Scrape images first.</Text>
                        ) : taggerImages && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                                {taggerImages.images.map((img) => (
                                    <TaggerImageCard
                                        key={img.filename}
                                        img={img}
                                        tag={taggerTags[img.filename] || 'untagged'}
                                        onTag={handleTag}
                                        theme={THEME}
                                        bbox={taggerBBoxes[img.filename]}
                                        onClickImage={(clickedImg) => setBboxEditorImg(clickedImg)}
                                        selected={selectedImages.has(img.filename)}
                                        onSelect={handleSelectImage}
                                    />
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            )}

            {bboxEditorImg && (
                <BBoxEditorModal
                    img={bboxEditorImg}
                    bbox={taggerBBoxes[bboxEditorImg.filename]}
                    onSave={handleSaveBBox}
                    onClose={() => setBboxEditorImg(null)}
                    theme={THEME}
                />
            )}

            {/* Recent Detections */}
            <div style={cardStyle}>
                <Text style={{ fontSize: 16, fontWeight: 600, color: THEME.textPrimary, marginBottom: 12 }}>
                    Recent Detections ({detections.length})
                </Text>
                {detections.length === 0 ? (
                    <Text style={{ color: THEME.textMuted, fontSize: 14 }}>No detections yet. Process stories to see results.</Text>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${THEME.border}` }}>
                                    {['Brand', 'Product', 'Author', 'Confidence', 'Sentiment', 'Time'].map(h => (
                                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {detections.map(d => (
                                    <tr key={d.id} style={{ borderBottom: `1px solid ${THEME.border}22` }}>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: THEME.textPrimary, fontWeight: 600 }}>{d.brand_name}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: THEME.textPrimary }}>{d.product_name}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: THEME.textMuted }}>{d.author_username || '-'}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: d.confidence_score > 0.7 ? '#22c55e' : '#ffc800' }}>
                                            {(d.confidence_score * 100).toFixed(0)}%
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: d.contextual_sentiment === 'positive_advocacy' ? 'rgba(34,197,94,0.15)' : d.contextual_sentiment === 'negative_complaint' ? 'rgba(255,68,68,0.15)' : 'rgba(255,255,255,0.08)', color: d.contextual_sentiment === 'positive_advocacy' ? '#22c55e' : d.contextual_sentiment === 'negative_complaint' ? '#ff4444' : THEME.textMuted }}>
                                                {d.contextual_sentiment?.replace('_', ' ') || '-'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: THEME.textMuted }}>
                                            {d.processed_at ? new Date(d.processed_at).toLocaleString() : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </View>
    );
}

export default AdminPanelScreen;
